/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// codex.md v4 cleanup pending — legacy SVG chart component defs
// (ComboChart / TriSeriesMonthlyChart / LineChart / MonthlyBillingChart /
// HeadcountStackedChart / FinancialTriAxisSection / RevenueCostWaterfall /
// QuarterComboChart / QuarterLineChart / MiniGroupedBarChart) stay
// in-file for one release so rollback = single commit revert. Phase 7
// follow-up strips them.
import { useCallback, useEffect, useMemo, useState } from "react";

import { C } from "../../../../constants";
import { uid } from "../../../../utils";
import apiClient from "../../../../services/api";
import { useAuth } from "../../../../app/providers/AuthProvider";

// codex.md v4 — new chart stack. Tremor for every line/bar/KPI; Nivo
// only for the three advanced visuals (Headcount stack, Waterfall,
// Utilisation Heatmap). Every data transform goes through an adapter
// in src/shared/charts/adapters.js — no render-time computation.
import {
  toRevenueRows, toCostRows, toProfitRows,
  toBillingBarRows, toProfitMarginRows,
  toUtilisationRows, toHourlyRateRows,
  toHeadcountRows, toWaterfallRows,
  toHeatmapRows, toTopClientsBars, toPipelineBars,
} from "../../../../shared/charts/adapters";
import {
  RevenueChart, CostChart, ProfitChart,
} from "../charts/RevenueCostProfitCharts";
import MonthlyBillingChartV2 from "../charts/MonthlyBillingChart";
import ProfitMarginChartV2 from "../charts/ProfitMarginChart";
import UtilisationTrendChart from "../charts/UtilisationTrendChart";
import AverageHourlyRateChart from "../charts/AverageHourlyRateChart";
import NpsChartV2 from "../charts/NpsChart";
import TopClientsChart from "../charts/TopClientsChart";
import PipelineChartV2 from "../charts/PipelineChart";
import HeadcountChartV2 from "../charts/HeadcountChart";
import WaterfallChartV2 from "../charts/WaterfallChart";
import UtilisationHeatmap from "../charts/UtilisationHeatmap";
import MonthlyFinancialsEditor from "../charts/MonthlyFinancialsEditor";

let CURRENT_USER = { name: "" };

function KpiCard({label,value,sub,trend,up,onClick,accent}){return <div className={`kpi${onClick?" click":""}`} onClick={onClick} style={accent?{borderTopColor:accent,borderTopWidth:2}:{}}><div className="kpi-lbl">{label}</div><div className="kpi-val" style={accent?{color:accent}:{}}>{value}</div>{trend&&<div className="kpi-trend" style={{color:up?C.green:C.red}}><span>{up?"▲":"▼"}</span><span>{trend}</span></div>}{sub&&<div className="kpi-sub">{sub}</div>}</div>;}

// codex.md v3: PM and HoT carry the admin badge (for Resources-page
// hiding, drawer chips, etc.) but DO contribute to analytics. Every
// rollup (FT+PT Avg Rate, headcount, utilisation averages) keys off
// this helper so the two exceptions live in one place.
const ANALYTICS_INCLUDED_ADMIN_ROLES = new Set(['PM', 'HoT']);

function isNonBillableConsultant(c) {
  if (c?.adminRole && ANALYTICS_INCLUDED_ADMIN_ROLES.has(c.adminRole)) return false;
  return Boolean(c?.isAdmin) || Boolean(c?.adminRole);
}

// True if the consultant should contribute to calculations AS OF `asOfDate`.
// Returns false when the consultant is flagged inactive on or before that
// date (Resources page lets a PM retire a consultant with an effective
// date). Non-billable admins are skipped regardless of the date, matching
// isNonBillableConsultant semantics above.
function isActiveConsultantOn(c, asOfDate) {
  if (!c) return false;
  if (isNonBillableConsultant(c)) return false;
  if (!c.inactiveSince) return true;
  const cutoff = new Date(c.inactiveSince).getTime();
  if (!isFinite(cutoff)) return true;
  const when = asOfDate instanceof Date ? asOfDate.getTime() : new Date(asOfDate).getTime();
  return when < cutoff;
}
function fmtSekK(v){if(v===null||v===undefined)return "—";const k=v/1000;return Math.abs(k)>=100?`${Math.round(k).toLocaleString()}K kr`:`${k.toFixed(1)}K kr`;}
function LegacyChip({status}){return <span className="chip" style={{fontSize:10.5,padding:"3px 9px",borderRadius:999,border:"1px solid #14213d",background:"#0e1c38",color:"#8fa4c8"}}>{status}</span>;}
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const REPORT_DEPTS = [
  { id:"finance",       label:"Finance",          head:"Lisa Park",    icon:"◬", placeholder:"Summarise revenue performance, P&L highlights, cost variances and outlook for next month." },
  { id:"sales",         label:"Sales",            head:"Marcus Webb",  icon:"◎", placeholder:"Pipeline activity, new wins, conversion rates, deals lost and forecast update." },
  { id:"delivery",      label:"Delivery",         head:"Tom Rivet",    icon:"▣", placeholder:"Project delivery status, utilisation rate, blockers, resourcing and upcoming milestones." },
  { id:"clientsuccess", label:"Client Success",   head:"Sarah Chen",   icon:"◑", placeholder:"NPS scores, client feedback, escalations, renewals and key relationship updates." },
  { id:"operations",    label:"Operations",       head:"Amara Osei",   icon:"⚙", placeholder:"Processes, tooling, compliance, infrastructure and operational improvements." },
  { id:"people",        label:"People & Culture", head:"Kai Nakamura", icon:"◈", placeholder:"Headcount changes, hiring, attrition, wellbeing initiatives and culture highlights." },
];

// Monthly financial data for 2026
// Jan–Mar = actual (today is 24 Mar), Apr–Dec = forecast
const FINANCIAL_DATA = {
  utilisation: {
    actual:    [76, 81, 84, null, null, null, null, null, null, null, null, null],
    forecast:  [null, null, null, 86, 88, 87, 85, 84, 86, 82, 79, 80],
    target:    85, // % target line
  },
  billables: {
    // in thousands (€)
    actual:    [84, 93, 102, null, null, null, null, null, null, null, null, null],
    forecast:  [null, null, null, 108, 115, 112, 107, 104, 109, 98, 92, 96],
    target:    100,
  },
  avgHourlyRate: {
    // blended average € per hour across all active projects
    actual:    [171, 173, 174, null, null, null, null, null, null, null, null, null],
    forecast:  [null, null, null, 175, 176, 177, 176, 178, 179, 178, 180, 181],
    target:    175,
  },
};

// Per-project monthly rates for breakdown table
const PROJECT_RATE_DATA = [
  { name:"Core Banking Transformation", rate:185, util:88,  model:"T&M"       },
  { name:"Data Governance Framework",   rate:175, util:74,  model:"T&M"       },
  { name:"SAP Finance Module",          rate:165, util:62,  model:"Fixed Fee"  },
  { name:"Data Lakehouse Build",        rate:195, util:95,  model:"T&M"       },
  { name:"Claims Process Automation",   rate:155, util:45,  model:"Fixed Fee"  },
  { name:"Analytics Platform",          rate:175, util:91,  model:"T&M"       },
  { name:"Digital Identity Solution",   rate:160, util:70,  model:"T&M"       },
];

// Extended data for additional charts matching the PDF monthly report
const EXTENDED_FIN_DATA = {
  profitMargin: {
    actual:   [22, 26, 29, null, null, null, null, null, null, null, null, null],
    forecast: [null,null,null, 31, 33, 34, 32, 31, 33, 30, 28, 29],
    target: 30,
  },
  headcount: {
    employees: [18, 18, 19, null, null, null, null, null, null, null, null, null],
    contractors:[6,  7,  8, null, null, null, null, null, null, null, null, null],
    target: 22,
  },
  nps: {
    actual:   [7.4, 7.6, 7.8, null, null, null, null, null, null, null, null, null],
    forecast: [null,null,null, 7.9, 8.0, 8.1, 8.0, 8.1, 8.2, 8.1, 8.3, 8.4],
    target: 8.0,
  },
  pipelineActivity: {
    newOpps:  [3, 4, 3, null, null, null, null, null, null, null, null, null],
    closed:   [1, 2, 2, null, null, null, null, null, null, null, null, null],
    lost:     [1, 1, 0, null, null, null, null, null, null, null, null, null],
  },
  contractorCosts: {
    actual:   [48, 54, 62, null, null, null, null, null, null, null, null, null],
    forecast: [null,null,null, 68, 72, 70, 65, 63, 67, 60, 57, 59],
    target: 65,
  },
};

// ── Shared chart primitives ────────────────────────────────────────────────

const CHART_CSS = `
.fin-page{padding:28px 36px 52px;}
.fin-krow{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:22px;}
.chart-card{background:#0a1528;border:1px solid #14213d;border-radius:12px;padding:22px 24px;margin-bottom:18px;}
.chart-title{font-size:13px;font-weight:700;color:#dce8f8;margin-bottom:3px;}
.chart-sub{font-size:11.5px;color:#8fa4c8;margin-bottom:20px;}
.chart-wrap{width:100%;overflow:hidden;}
.chart-legend{display:flex;align-items:center;gap:18px;margin-top:14px;flex-wrap:wrap;}
.legend-item{display:flex;align-items:center;gap:7px;font-size:11.5px;color:#8fa4c8;}
.legend-swatch{width:12px;height:4px;border-radius:2px;flex-shrink:0;}
.legend-swatch.dashed{height:0;border-top:2px dashed currentColor;width:16px;}
.chart-tabs{display:flex;gap:2px;margin-bottom:18px;background:#070e20;border:1px solid #14213d;border-radius:8px;padding:3px;width:fit-content;}
.chart-tab{padding:6px 14px;border-radius:6px;cursor:pointer;font-size:12px;font-weight:500;color:#8fa4c8;transition:all .12s;user-select:none;}
.chart-tab:hover{color:#c4d4ee;}
.chart-tab.active{background:rgba(59,127,250,.14);color:#60a5fa;}
.rate-table{width:100%;border-collapse:collapse;margin-top:4px;}
.rate-table th{font-size:10px;font-weight:700;color:#8fa4c8;text-transform:uppercase;letter-spacing:.1em;padding:8px 12px;text-align:left;border-bottom:1px solid #14213d;}
.rate-table td{padding:10px 12px;border-bottom:1px solid #0b1526;font-size:13px;vertical-align:middle;color:#dce8f8;}
.rate-table tr:last-child td{border-bottom:none;}
.rate-table tr:hover td{background:#0e1c38;}
.delta-up{color:#10c981;font-size:11px;font-weight:700;}
.delta-dn{color:#f04444;font-size:11px;font-weight:700;}
.insight-row{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:18px;}
.insight-card{background:#070e20;border:1px solid #14213d;border-radius:9px;padding:14px 16px;}
.insight-lbl{font-size:9.5px;font-weight:700;color:#364f72;text-transform:uppercase;letter-spacing:.1em;margin-bottom:8px;}
.insight-val{font-size:19px;font-weight:800;font-family:'Noto Sans Mono',monospace;}
.insight-sub{font-size:11px;color:#8fa4c8;margin-top:4px;}
.fin-indicative{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;color:#6889b4;background:rgba(59,127,250,.07);border:1px solid rgba(59,127,250,.15);border-radius:4px;padding:1px 6px;margin-left:8px;vertical-align:middle;letter-spacing:.02em;}
/* Phase 5p: mr-* (Monthly Report banner + dept grid + buttons) and
 * pdf-* (PDF preview modal) rules removed from this inline stylesheet —
 * JSX callsites migrated to Tailwind utilities inline. */
`;

// Injects chart CSS once alongside main CSS — we append to the existing style block
// by including it as part of the component mount

function useChartCSS() {
  useEffect(()=>{
    const el=document.createElement("style");
    el.textContent=CHART_CSS;
    document.head.appendChild(el);
    return()=>{try{document.head.removeChild(el);}catch(_){}};
  },[]);
}

// ── SVG Bar + Line combo chart ──────────────────────────────────────────────

function ComboChart({
  actual, forecast, target,
  unit="", label="", height=180,
  actualColor="#3b7ffa", forecastColor="#60a5fa",
  targetColor="#f59e0b", areaOpacity=0.13,
}) {
  const W = 780, H = height;
  const PAD = { top:20, right:52, bottom:36, left:52 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top  - PAD.bottom;

  const allVals = [...actual.filter(v=>v!==null), ...forecast.filter(v=>v!==null), target];
  const dataMin = allVals.length > 0 ? Math.min(...allVals) : 0;
  const dataMax = allVals.length > 0 ? Math.max(...allVals) : 100;
  const yMin = Math.max(0, Math.floor((dataMin * 0.88) / 10) * 10);
  let yMax = Math.ceil((dataMax * 1.08) / 10) * 10;
  if (unit === "%" && yMax < 120) yMax = 120; // utilisation often exceeds 100%, leave headroom
  const yRange = yMax - yMin || 1;

  const scaleY = v => PAD.top + innerH - ((v - yMin) / yRange) * innerH;
  // Center each month in its slot so the first/last bars don't overlap with Y-axis or right edge
  const slotW = innerW / MONTHS.length;
  const scaleX = i => PAD.left + (i + 0.5) * slotW;
  const barW   = slotW * 0.62;

  // Y axis ticks
  const yTicks = [];
  const tickStep = Math.ceil(yRange / 5 / 10) * 10 || 10;
  for (let t = yMin; t <= yMax + 0.01; t += tickStep) yTicks.push(t);

  // Forecast area path
  const fPoints = forecast
    .map((v,i)=>v!==null?{x:scaleX(i),y:scaleY(v)}:null)
    .filter(Boolean);
  let fAreaPath="", fLinePath="";
  if (fPoints.length > 0) {
    fLinePath = fPoints.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const bottom = scaleY(yMin);
    fAreaPath = fLinePath + ` L${fPoints[fPoints.length-1].x},${bottom} L${fPoints[0].x},${bottom} Z`;
  }

  // Actual line path
  const aPoints = actual.map((v,i)=>v!==null?{x:scaleX(i),y:scaleY(v)}:null).filter(Boolean);
  const aLinePath = aPoints.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // Target line
  const targetY = scaleY(target);
  const tLine = `M${PAD.left},${targetY.toFixed(1)} L${PAD.left+innerW},${targetY.toFixed(1)}`;

  // Last actual month index
  const lastActualIdx = actual.reduce((m,v,i)=>v!==null?i:m, -1);

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",display:"block"}} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`fg-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={forecastColor} stopOpacity={areaOpacity*2}/>
            <stop offset="100%" stopColor={forecastColor} stopOpacity="0"/>
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yTicks.map(t=>(
          <line key={t} x1={PAD.left} y1={scaleY(t).toFixed(1)} x2={PAD.left+innerW} y2={scaleY(t).toFixed(1)}
            stroke="#14213d" strokeWidth="1"/>
        ))}

        {/* Forecast area fill */}
        {fAreaPath && <path d={fAreaPath} fill={`url(#fg-${label})`}/>}

        {/* Actual bars */}
        {actual.map((v,i)=> v!==null ? (
          <rect key={i}
            x={(scaleX(i) - barW/2).toFixed(1)} y={scaleY(v).toFixed(1)}
            width={barW.toFixed(1)} height={(scaleY(yMin)-scaleY(v)).toFixed(1)}
            rx="3" fill={actualColor} opacity="0.9"/>
        ) : null)}

        {/* Forecast bars (lighter / outlined) */}
        {forecast.map((v,i)=> v!==null ? (
          <rect key={i}
            x={(scaleX(i) - barW/2).toFixed(1)} y={scaleY(v).toFixed(1)}
            width={barW.toFixed(1)} height={(scaleY(yMin)-scaleY(v)).toFixed(1)}
            rx="3" fill={forecastColor} opacity="0.35"/>
        ) : null)}

        {/* Forecast line */}
        {fLinePath && <path d={fLinePath} fill="none" stroke={forecastColor} strokeWidth="1.5" strokeDasharray="5,3" opacity="0.8"/>}

        {/* Actual line */}
        {aLinePath && <path d={aLinePath} fill="none" stroke={actualColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}

        {/* Target dashed line */}
        <path d={tLine} fill="none" stroke={targetColor} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7"/>
        <text x={PAD.left+innerW+4} y={targetY+4} fill={targetColor} fontSize="10" fontFamily="'Noto Sans Mono',monospace" opacity="0.85">target</text>

        {/* Actual dots */}
        {aPoints.map((p,i)=>(
          <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="4" fill={actualColor} stroke="#0a1528" strokeWidth="1.5"/>
        ))}

        {/* Forecast dots */}
        {fPoints.map((p,i)=>(
          <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="3" fill={forecastColor} stroke="#0a1528" strokeWidth="1.5" opacity="0.8"/>
        ))}

        {/* Value labels on actual bars */}
        {actual.map((v,i)=> v!==null ? (
          <text key={i} x={scaleX(i).toFixed(1)} y={(scaleY(v)-6).toFixed(1)}
            textAnchor="middle" fill={actualColor} fontSize="10" fontFamily="'Noto Sans Mono',monospace" fontWeight="600">
            {unit==="€K"?`${v}K kr`:unit==="%"?`${v}%`:`${v} kr`}
          </text>
        ) : null)}

        {/* "NOW" divider */}
        {lastActualIdx >= 0 && lastActualIdx < MONTHS.length-1 && (
          <>
            <line
              x1={((scaleX(lastActualIdx)+scaleX(lastActualIdx+1))/2).toFixed(1)} y1={PAD.top}
              x2={((scaleX(lastActualIdx)+scaleX(lastActualIdx+1))/2).toFixed(1)} y2={PAD.top+innerH}
              stroke="#1c2e52" strokeWidth="1" strokeDasharray="3,3"/>
            <text
              x={((scaleX(lastActualIdx)+scaleX(lastActualIdx+1))/2+4).toFixed(1)}
              y={(PAD.top+10).toFixed(1)}
              fill="#364f72" fontSize="9" fontFamily="'Noto Sans',sans-serif" letterSpacing="0.1em" >
              NOW
            </text>
          </>
        )}

        {/* Y axis labels */}
        {yTicks.map(t=>(
          <text key={t} x={PAD.left-6} y={(scaleY(t)+4).toFixed(1)}
            textAnchor="end" fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">
            {unit==="%"?`${t}%`:unit==="€K"?`${t}K kr`:`${t} kr`}
          </text>
        ))}

        {/* X axis labels */}
        {MONTHS.map((m,i)=>(
          <text key={i} x={scaleX(i).toFixed(1)} y={PAD.top+innerH+18}
            textAnchor="middle" fill={actual[i]!==null?"#8fa4c8":"#364f72"} fontSize="11" fontFamily="'Noto Sans',sans-serif" fontWeight={actual[i]!==null?"600":"400"}>
            {m}
          </text>
        ))}

        {/* X axis baseline */}
        <line x1={PAD.left} y1={PAD.top+innerH} x2={PAD.left+innerW} y2={PAD.top+innerH} stroke="#14213d" strokeWidth="1"/>
      </svg>
    </div>
  );
}

// ── SVG Line chart for avg hourly rate ─────────────────────────────────────

function LineChart({ actual, forecast, target, unit="kr", height=200 }) {
  const W=780, H=height;
  const PAD={top:20,right:60,bottom:36,left:52};
  const innerW=W-PAD.left-PAD.right;
  const innerH=H-PAD.top-PAD.bottom;

  const allVals=[...actual.filter(v=>v!==null),...forecast.filter(v=>v!==null),target];
  const yMin=Math.floor(Math.min(...allVals)*0.97/5)*5;
  const yMax=Math.ceil(Math.max(...allVals)*1.03/5)*5;
  const yRange=yMax-yMin||1;

  const sY=v=>PAD.top+innerH-((v-yMin)/yRange)*innerH;
  const sX=i=>PAD.left+(i/(MONTHS.length-1))*innerW;

  const yTicks=[];
  const step=Math.ceil(yRange/4/5)*5;
  for(let t=yMin;t<=yMax+0.01;t+=step)yTicks.push(t);

  const aPoints=actual.map((v,i)=>v!==null?{x:sX(i),y:sY(v),v}:null).filter(Boolean);
  const fPoints=forecast.map((v,i)=>v!==null?{x:sX(i),y:sY(v),v}:null).filter(Boolean);

  const aPath=aPoints.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const fPath=fPoints.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // Smooth area under actual line
  const aAreaPath = aPath + (aPoints.length>0?` L${aPoints[aPoints.length-1].x},${sY(yMin)} L${aPoints[0].x},${sY(yMin)} Z`:"");

  const lastActIdx=actual.reduce((m,v,i)=>v!==null?i:m,-1);
  const tY=sY(target);

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",display:"block"}} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lc-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10c981" stopOpacity="0.18"/>
            <stop offset="100%" stopColor="#10c981" stopOpacity="0"/>
          </linearGradient>
        </defs>
        {yTicks.map(t=>(
          <line key={t} x1={PAD.left} y1={sY(t).toFixed(1)} x2={PAD.left+innerW} y2={sY(t).toFixed(1)} stroke="#14213d" strokeWidth="1"/>
        ))}
        {aAreaPath&&<path d={aAreaPath} fill="url(#lc-area)"/>}
        {fPath&&<path d={fPath} fill="none" stroke="#60a5fa" strokeWidth="2" strokeDasharray="5,3" opacity="0.8"/>}
        {aPath&&<path d={aPath} fill="none" stroke="#10c981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
        {/* Target */}
        <line x1={PAD.left} y1={tY.toFixed(1)} x2={PAD.left+innerW} y2={tY.toFixed(1)} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7"/>
        <text x={PAD.left+innerW+4} y={tY+4} fill="#f59e0b" fontSize="10" fontFamily="'Noto Sans Mono',monospace" opacity="0.85">target</text>
        {/* Dots */}
        {aPoints.map((p,i)=>(
          <g key={i}>
            <circle cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="4.5" fill="#10c981" stroke="#0a1528" strokeWidth="2"/>
            <text x={p.x.toFixed(1)} y={(p.y-10).toFixed(1)} textAnchor="middle" fill="#10c981" fontSize="10" fontFamily="'Noto Sans Mono',monospace" fontWeight="600">
              {unit}{p.v}
            </text>
          </g>
        ))}
        {fPoints.map((p,i)=>(
          <g key={i}>
            <circle cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="3.5" fill="#60a5fa" stroke="#0a1528" strokeWidth="1.5" opacity="0.8"/>
            {i===fPoints.length-1&&(
              <text x={p.x.toFixed(1)} y={(p.y-10).toFixed(1)} textAnchor="middle" fill="#60a5fa" fontSize="10" fontFamily="'Noto Sans Mono',monospace">{unit}{p.v}</text>
            )}
          </g>
        ))}
        {/* NOW divider */}
        {lastActIdx>=0&&lastActIdx<MONTHS.length-1&&(
          <line
            x1={((sX(lastActIdx)+sX(lastActIdx+1))/2).toFixed(1)} y1={PAD.top}
            x2={((sX(lastActIdx)+sX(lastActIdx+1))/2).toFixed(1)} y2={PAD.top+innerH}
            stroke="#1c2e52" strokeWidth="1" strokeDasharray="3,3"/>
        )}
        {yTicks.map(t=>(
          <text key={t} x={PAD.left-6} y={(sY(t)+4).toFixed(1)} textAnchor="end" fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">
            {unit}{t}
          </text>
        ))}
        {MONTHS.map((m,i)=>(
          <text key={i} x={sX(i).toFixed(1)} y={PAD.top+innerH+18}
            textAnchor="middle" fill={actual[i]!==null?"#8fa4c8":"#364f72"} fontSize="11" fontFamily="'Noto Sans',sans-serif" fontWeight={actual[i]!==null?"600":"400"}>
            {m}
          </text>
        ))}
        <line x1={PAD.left} y1={PAD.top+innerH} x2={PAD.left+innerW} y2={PAD.top+innerH} stroke="#14213d" strokeWidth="1"/>
      </svg>
    </div>
  );
}

// ── Spark mini bar (inline) ────────────────────────────────────────────────

function SparkBar({ val, max, color }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:7}}>
      <div style={{flex:1,height:5,background:"#14213d",borderRadius:3,overflow:"hidden"}}>
        <div style={{width:`${(val/max)*100}%`,height:"100%",background:color,borderRadius:3}}/>
      </div>
      <span className="mono" style={{fontSize:11,color,minWidth:32,textAlign:"right"}}>{val}%</span>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

// ─── Derive Contract from active projects ──────────────────────────────────
// For each month, sum (ratePerHour × hoursPerWeek × ~4.33) for projects whose
// contractStartDate–contractEndDate span that month.
const MONTHS_IDX=["January","February","March","April","May","June","July","August","September","October","November","December"];
function deriveMonthlyContract(projects){
  const yr=2026;
  return MONTHS_IDX.map((_,mi)=>{
    const mStart=new Date(yr,mi,1);
    const mEnd  =new Date(yr,mi+1,0);
    return projects.filter(p=>{
      if(p.projectStatus==="Cancelled")return false;
      const s=new Date(p.contractStartDate), e=new Date(p.contractEndDate);
      return s<=mEnd && e>=mStart;
    }).reduce((sum,p)=>{
      const monthly=Math.round(p.ratePerHour*(p.hoursPerWeek||40)*4.33);
      return sum+monthly;
    },0);
  });
}

// ─── Derive Forecast 50% from scoping pipeline + leads ──────────────────────
// Active scoping cards (not Done/Lost) with dealValue → spread evenly across
// startDate..endDate (or 3 months from now), weighted at 50%.
// Leads with estimatedValue → spread across next 3 months at 25%.
function deriveForecast50(scoping, leads){
  const yr=2026;
  const monthly=new Array(12).fill(0);
  // Scoping / pipeline cards with dealValue
  (scoping||[]).forEach(s=>{
    if(s.stage?.includes("Done")||s.stage?.includes("Lost"))return;
    const val=s.dealValue||0;
    if(val<=0)return;
    const prob=(s.probability||50)/100;
    let sM=0, eM=11;
    if(s.startDate){const d=new Date(s.startDate);if(d.getFullYear()===yr)sM=d.getMonth();}
    if(s.endDate){const d=new Date(s.endDate);if(d.getFullYear()===yr)eM=d.getMonth();}
    else eM=Math.min(11,sM+2);
    const span=Math.max(1,eM-sM+1);
    const perMonth=Math.round(val*prob/span);
    for(let m=sM;m<=eM;m++) monthly[m]+=perMonth;
  });
  // Leads with estimatedValue → 25% weight spread across next 3 months
  const now=new Date();
  const curM=now.getMonth();
  (leads||[]).forEach(l=>{
    if(l.status==="LOST")return;
    const raw=(l.estimatedValue||"").replace(/[^0-9.]/g,"");
    let val=parseFloat(raw)||0;
    if(l.estimatedValue?.includes("K"))val*=1000;
    if(val<=0)return;
    const perMonth=Math.round(val*0.25/3);
    for(let m=curM;m<Math.min(12,curM+3);m++) monthly[m]+=perMonth;
  });
  return monthly;
}

// Seed data for editable fields (actual billing, target, break-even, costs)
const BILLING_ACTUAL_SEED  =[920000,1100000,730000,472370,0,0,0,0,0,0,0,0];
const BILLING_TARGET_SEED  =[1302246,1647534,1608032,2196020,1952748,2129616,2298574,2055814,2608844,2587458,2786868,1834902];
const COSTS_SEED           =[1873000,2000000,1995000,2168553,2156000,2256019,2256000,2329619,2310000,2368881,2429000,2472701];
const BREAK_EVEN_SEED      =1200000;

function MonthlyBillingChart({data=[], breakEven=1200000}){
  // H shrunk from 420 → 320 per codex.md chart-sizing pass. viewBox keeps
  // the chart responsive; the shorter intrinsic height just reduces the
  // reserved vertical space in the page.
  const W=1280, H=320;
  const padL=60, padR=20, padT=40, padB=60;
  const plotW=W-padL-padR, plotH=H-padT-padB;
  const maxV=3000000;
  const yTicks=[0,500000,1000000,1500000,2000000,2500000,3000000];
  const y=v=>padT+plotH-(v/maxV)*plotH;
  const groupW=plotW/data.length;
  const barW=groupW*0.18;
  const fmt=v=>v>=1000?v.toLocaleString():v;
  const C_CONTRACT="#3b7ffa", C_FORECAST="#f97316", C_ACTUAL="#fbbf24", C_TARGET="#a78bfa";

  return (
    <div style={{width:"100%",overflowX:"auto"}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",minWidth:900,display:"block"}}>
        {/* Y gridlines + labels */}
        {yTicks.map(t=>(
          <g key={t}>
            <line x1={padL} x2={W-padR} y1={y(t)} y2={y(t)} stroke="#14213d" strokeWidth="1"/>
            <text x={padL-8} y={y(t)+4} fill="#6889b4" fontSize="11" textAnchor="end" fontFamily="'Noto Sans',sans-serif">{t.toLocaleString()}</text>
          </g>
        ))}
        {/* Break-even dashed line */}
        <line x1={padL} x2={W-padR} y1={y(breakEven)} y2={y(breakEven)} stroke="#10c981" strokeWidth="1.5" strokeDasharray="6,5"/>
        <text x={padL+4} y={y(breakEven)-5} fill="#10c981" fontSize="11" fontFamily="'Noto Sans',sans-serif">Break Even</text>

        {/* Bar groups */}
        {data.map((d,i)=>{
          const cx=padL+i*groupW+groupW/2;
          const gap=barW*0.25;
          // Stacked bar (Contract + Forecast) centered-left of group
          const stackX=cx-barW*1.5-gap;
          const contractH=(d.contract/maxV)*plotH;
          const forecastH=(d.forecast/maxV)*plotH;
          const contractY=padT+plotH-contractH;
          const forecastY=contractY-forecastH;
          // Actual bar
          const actualX=cx-barW/2;
          const actualH=(d.actual/maxV)*plotH;
          const actualY=padT+plotH-actualH;
          // Target bar
          const targetX=cx+barW/2+gap;
          const targetH=(d.target/maxV)*plotH;
          const targetY=padT+plotH-targetH;
          const total=d.contract+d.forecast;
          return (
            <g key={d.m}>
              {/* Contract (bottom of stack) */}
              {d.contract>0&&<rect x={stackX} y={contractY} width={barW} height={contractH} fill={C_CONTRACT} rx="2"/>}
              {/* Forecast 50% (top of stack) */}
              {d.forecast>0&&<rect x={stackX} y={forecastY} width={barW} height={forecastH} fill={C_FORECAST} rx="2"/>}
              {/* Actual */}
              {d.actual>0&&<rect x={actualX} y={actualY} width={barW} height={actualH} fill={C_ACTUAL} rx="2"/>}
              {/* Target */}
              {d.target>0&&<rect x={targetX} y={targetY} width={barW} height={targetH} fill={C_TARGET} rx="2"/>}

              {/* Labels on top of each bar */}
              {total>0&&<text x={stackX+barW/2} y={forecastY-5} fill="#8fa4c8" fontSize="10" textAnchor="middle" fontFamily="'Noto Sans',sans-serif">{fmt(total)}</text>}
              {d.actual>0&&<text x={actualX+barW/2} y={actualY-5} fill="#8fa4c8" fontSize="10" textAnchor="middle" fontFamily="'Noto Sans',sans-serif">{fmt(d.actual)}</text>}
              {d.target>0&&<text x={targetX+barW/2} y={targetY-5} fill="#8fa4c8" fontSize="10" textAnchor="middle" fontFamily="'Noto Sans',sans-serif">{fmt(d.target)}</text>}

              {/* Month label */}
              <text x={cx} y={H-padB+20} fill="#8fa4c8" fontSize="12" textAnchor="middle" fontFamily="'Noto Sans',sans-serif" fontWeight="600">{d.m}</text>
            </g>
          );
        })}
        {/* X axis baseline */}
        <line x1={padL} x2={W-padR} y1={padT+plotH} y2={padT+plotH} stroke="#14213d" strokeWidth="1.5"/>
      </svg>
    </div>
  );
}

// Revenue seed for editable overrides (derived from billing contract+forecast+actual by default)
const REVENUE_SEED=[1483687,1771000,1819713,2412016,2130000,2310000,2568575,2290000,2878845,2810000,3046069,2085463];

// codex.md Financial Board — single-axis chart with three monthly series.
// Solid bars/line = Actual (tracker-authoritative for revenue; CFO-manual
// for cost). Lighter bars = Expected (CFO plan). Dotted line = Forecast
// (CFO projection). Used 3x on the page: Revenue, Cost, Profit.
function TriSeriesMonthlyChart({
  actual   = [],   // e.g. revenue.actual per month  (12-length, nulls allowed)
  expected = [],   // revenue.expected per month
  forecast = [],   // revenue.forecast per month
  monthLabels = [], // e.g. ["Jan", "Feb", ...]
  unit = "kr",
  height = 240,
  actualColor   = "#3b7ffa",
  expectedColor = "#94b8f0",
  forecastColor = "#f59e0b",
}) {
  const W = 780, H = height;
  const PAD = { top: 20, right: 40, bottom: 36, left: 60 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top  - PAD.bottom;
  const n = Math.max(actual.length, expected.length, forecast.length, monthLabels.length, 12);
  const labels = monthLabels.length === n ? monthLabels : Array.from({length: n}, (_,i)=>String(i+1));

  const vals = [...actual, ...expected, ...forecast].filter((v) => v !== null && v !== undefined && isFinite(v));
  const hasData = vals.length > 0;
  const dataMin = hasData ? Math.min(0, ...vals) : 0; // include 0 for profit negatives
  const dataMax = hasData ? Math.max(...vals) : 1;
  const pad = Math.abs(dataMax - dataMin) * 0.12 || 1;
  const yMin = Math.floor((dataMin - (dataMin < 0 ? pad : 0)) / 1000) * 1000;
  const yMax = Math.ceil((dataMax + pad) / 1000) * 1000;
  const yRange = (yMax - yMin) || 1;

  const sY = (v) => PAD.top + innerH - ((v - yMin) / yRange) * innerH;
  const slotW = innerW / n;
  const sX = (i) => PAD.left + (i + 0.5) * slotW;
  const barW = slotW * 0.28;

  // Y ticks — ~5 divisions rounded to the nearest 1000.
  const step = Math.max(1000, Math.ceil(yRange / 5 / 1000) * 1000);
  const yTicks = [];
  for (let t = Math.floor(yMin / step) * step; t <= yMax + 0.01; t += step) yTicks.push(t);

  const zeroY = sY(0);
  const fmt = (v) => {
    if (v === null || v === undefined || !isFinite(v)) return "";
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${Math.round(v / 1000)}K`;
    return String(Math.round(v));
  };

  // Line path helpers.
  const linePath = (arr) => arr
    .map((v, i) => (v === null || v === undefined ? null : { x: sX(i), y: sY(v) }))
    .filter(Boolean)
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");

  const aPath = linePath(actual);
  const ePath = linePath(expected);
  const fPath = linePath(forecast);

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",display:"block"}} xmlns="http://www.w3.org/2000/svg">
        {/* Grid lines */}
        {yTicks.map((t) => (
          <line key={t} x1={PAD.left} x2={PAD.left+innerW} y1={sY(t).toFixed(1)} y2={sY(t).toFixed(1)}
            stroke="#14213d" strokeWidth="1" strokeDasharray={t===0?"":"2,3"}/>
        ))}
        {/* Zero line emphasised */}
        <line x1={PAD.left} x2={PAD.left+innerW} y1={zeroY.toFixed(1)} y2={zeroY.toFixed(1)}
          stroke="#2a3f66" strokeWidth="1"/>

        {/* Expected bars — lighter fill, rendered first so Actual sits on top */}
        {expected.map((v, i) => (v === null || v === undefined) ? null : (
          <rect key={`e-${i}`}
            x={(sX(i) - barW*1.5 - 2).toFixed(1)}
            y={Math.min(zeroY, sY(v)).toFixed(1)}
            width={barW.toFixed(1)}
            height={Math.abs(sY(v) - zeroY).toFixed(1)}
            rx="2" fill={expectedColor} opacity="0.45"/>
        ))}
        {/* Actual bars */}
        {actual.map((v, i) => (v === null || v === undefined) ? null : (
          <rect key={`a-${i}`}
            x={(sX(i) - barW/2).toFixed(1)}
            y={Math.min(zeroY, sY(v)).toFixed(1)}
            width={barW.toFixed(1)}
            height={Math.abs(sY(v) - zeroY).toFixed(1)}
            rx="2" fill={actualColor} opacity="0.92"/>
        ))}

        {/* Lines */}
        {ePath && <path d={ePath} fill="none" stroke={expectedColor} strokeWidth="1.5" opacity="0.9"/>}
        {aPath && <path d={aPath} fill="none" stroke={actualColor}   strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
        {fPath && <path d={fPath} fill="none" stroke={forecastColor} strokeWidth="2"   strokeDasharray="5,3" opacity="0.9"/>}

        {/* Dots on actual */}
        {actual.map((v, i) => (v === null || v === undefined) ? null : (
          <circle key={`ad-${i}`} cx={sX(i).toFixed(1)} cy={sY(v).toFixed(1)} r="3" fill={actualColor} stroke="#0a1528" strokeWidth="1.5"/>
        ))}

        {/* Y tick labels */}
        {yTicks.map((t) => (
          <text key={`yl-${t}`} x={PAD.left - 8} y={(sY(t)+4).toFixed(1)} textAnchor="end"
            fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">{fmt(t)}{unit?` ${unit}`:""}</text>
        ))}
        {/* X labels */}
        {labels.map((m, i) => (
          <text key={`xl-${i}`} x={sX(i).toFixed(1)} y={PAD.top+innerH+18} textAnchor="middle"
            fill="#8fa4c8" fontSize="11" fontFamily="'Noto Sans',sans-serif" fontWeight="600">{m}</text>
        ))}
        {/* Baseline */}
        <line x1={PAD.left} x2={PAD.left+innerW} y1={(PAD.top+innerH).toFixed(1)} y2={(PAD.top+innerH).toFixed(1)} stroke="#14213d" strokeWidth="1"/>
      </svg>
    </div>
  );
}

// codex.md Phase 6 — headcount stacked column, Fulltime / Talent Hub / Non-billables.
// Pattern mirrors MiniGroupedBarChart but stacks vertically; shares sizing
// with TriSeriesMonthlyChart so the page's charts are visually consistent.
function HeadcountStackedChart({
  fulltime = [], talentHub = [], nonBillables = [], monthLabels = [], height = 220,
}) {
  const W = 780, H = height;
  const PAD = { top: 18, right: 30, bottom: 34, left: 44 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top  - PAD.bottom;
  const n = Math.max(fulltime.length, talentHub.length, nonBillables.length, monthLabels.length, 12);
  const labels = monthLabels.length === n ? monthLabels : Array.from({length: n}, (_,i)=>String(i+1));

  const totals = Array.from({length: n}, (_, i) =>
    (fulltime[i] || 0) + (talentHub[i] || 0) + (nonBillables[i] || 0)
  );
  const yMax = Math.max(1, Math.ceil(Math.max(...totals) * 1.1 / 5) * 5);
  const sY = (v) => PAD.top + innerH - (v / yMax) * innerH;
  const slotW = innerW / n;
  const sX = (i) => PAD.left + (i + 0.5) * slotW;
  const barW = slotW * 0.58;

  const C_FT = "#3b7ffa";
  const C_TH = "#f59e0b";
  const C_NB = "#94a3b8";

  // Dedup — when yMax is small the rounded quartiles collapse (e.g.
  // yMax=1 → [0,0,1,1,1]) and React flags duplicate keys. Set + sort
  // produces the same visual without the warning.
  const ticks = Array.from(new Set([0, Math.round(yMax/4), Math.round(yMax/2), Math.round(3*yMax/4), yMax])).sort((a,b)=>a-b);

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",display:"block"}} xmlns="http://www.w3.org/2000/svg">
        {/* Grid */}
        {ticks.map(t => (
          <line key={t} x1={PAD.left} x2={PAD.left+innerW} y1={sY(t).toFixed(1)} y2={sY(t).toFixed(1)}
            stroke="#14213d" strokeWidth="1"/>
        ))}
        {/* Stacked bars — bottom = fulltime, middle = talentHub, top = nonBillables */}
        {Array.from({length: n}, (_, i) => {
          const ft = fulltime[i] || 0;
          const th = talentHub[i] || 0;
          const nb = nonBillables[i] || 0;
          const ftY = sY(ft);
          const thY = sY(ft + th);
          const nbY = sY(ft + th + nb);
          const x = (sX(i) - barW/2);
          return (
            <g key={i}>
              {ft > 0 && <rect x={x.toFixed(1)} y={ftY.toFixed(1)} width={barW.toFixed(1)} height={Math.max(0, sY(0) - ftY).toFixed(1)} fill={C_FT} opacity="0.92"/>}
              {th > 0 && <rect x={x.toFixed(1)} y={thY.toFixed(1)} width={barW.toFixed(1)} height={Math.max(0, ftY - thY).toFixed(1)} fill={C_TH} opacity="0.92"/>}
              {nb > 0 && <rect x={x.toFixed(1)} y={nbY.toFixed(1)} width={barW.toFixed(1)} height={Math.max(0, thY - nbY).toFixed(1)} fill={C_NB} opacity="0.92"/>}
              {/* Total label on top */}
              {totals[i] > 0 && (
                <text x={sX(i).toFixed(1)} y={(nbY - 4).toFixed(1)} textAnchor="middle"
                  fill="#cbd5e1" fontSize="10" fontFamily="'Noto Sans',sans-serif" fontWeight="700">
                  {totals[i]}
                </text>
              )}
            </g>
          );
        })}
        {/* Y tick labels */}
        {ticks.map(t => (
          <text key={`yl-${t}`} x={PAD.left - 6} y={(sY(t) + 3).toFixed(1)} textAnchor="end"
            fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">{t}</text>
        ))}
        {/* X labels */}
        {labels.map((m, i) => (
          <text key={`xl-${i}`} x={sX(i).toFixed(1)} y={(PAD.top+innerH+16).toFixed(1)} textAnchor="middle"
            fill="#8fa4c8" fontSize="11" fontFamily="'Noto Sans',sans-serif" fontWeight="600">{m}</text>
        ))}
        {/* Baseline */}
        <line x1={PAD.left} x2={PAD.left+innerW} y1={(PAD.top+innerH).toFixed(1)} y2={(PAD.top+innerH).toFixed(1)} stroke="#14213d" strokeWidth="1"/>
      </svg>
    </div>
  );
}

/**
 * codex.md Financial Board — three graphs (Revenue, Cost, Profit), each with
 * three series (Actual, Expected, Forecast). Revenue.Actual is tracker-owned
 * (invoiceSync cron); rendered read-only with a 🔒 badge. Cost.Actual and
 * both *.Expected/Forecast are CFO-editable via PATCH /financial-status/monthly.
 * Profit is derived on read, never stored.
 */
function FinancialTriAxisSection({ year, monthlyFin, reloadMonthlyFin, canSeeGraph, showToast, canEdit = true }) {
  const [editOpen, setEditOpen] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const data = Array.isArray(monthlyFin) ? monthlyFin : [];

  // Flatten into 12-length per-series arrays once; charts + editor share them.
  // revActualPaid and revActualPending are the tracker-owned halves; the
  // chart plots `revActualTotal = paid + pending` as the Actual series so
  // postpaid months don't read as zero while the client still owes.
  const series = useMemo(() => {
    const byMonth = new Map(data.map((r) => [r.month, r]));
    const empty = () => Array(12).fill(null);
    const revActualPaid = empty(), revActualPending = empty(), revActualTotal = empty();
    const revExpected = empty(), revForecast = empty();
    const costActual = empty(), costExpected = empty(), costForecast = empty();
    const proActual = empty(), proExpected = empty(), proForecast = empty();
    for (let i = 0; i < 12; i++) {
      const row = byMonth.get(i + 1);
      if (!row) continue;
      revActualPaid[i]    = row.revenue?.actual        ?? null;
      revActualPending[i] = row.revenue?.actualPending ?? null;
      revActualTotal[i]   = row.revenue?.actualTotal   ?? null;
      revExpected[i] = row.revenue?.expected ?? null;
      revForecast[i] = row.revenue?.forecast ?? null;
      costActual[i]   = row.cost?.actual   ?? null;
      costExpected[i] = row.cost?.expected ?? null;
      costForecast[i] = row.cost?.forecast ?? null;
      proActual[i]   = row.profit?.actual   ?? null;
      proExpected[i] = row.profit?.expected ?? null;
      proForecast[i] = row.profit?.forecast ?? null;
    }
    return {
      revActualPaid, revActualPending, revActualTotal,
      revExpected, revForecast,
      costActual, costExpected, costForecast,
      proActual, proExpected, proForecast,
    };
  }, [data]);

  // Save a single (year, month, field) on blur. Null clears the cell.
  async function saveCell(month, field, raw) {
    const key = `${month}|${field}`;
    setSavingKey(key);
    try {
      const value = raw === "" || raw === null || raw === undefined ? null : Number(raw);
      if (value !== null && !isFinite(value)) throw new Error("not a number");
      await apiClient.updateMonthlyFinancials({ year, month, [field]: value });
      await reloadMonthlyFin();
    } catch (err) {
      showToast?.("Error", err?.response?.data?.error || "Failed to save");
    } finally {
      setSavingKey(null);
    }
  }

  const [syncing, setSyncing] = useState(false);
  async function runInvoiceSync() {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await apiClient.syncInvoicesNow();
      showToast?.("Invoice sync complete", `${result?.months ?? 0} months · ${result?.updated ?? 0} updated · ${result?.created ?? 0} created`);
      await reloadMonthlyFin();
    } catch (err) {
      showToast?.("Error", err?.response?.data?.error || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  if (monthlyFin === null) {
    return (
      <div className="chart-card">
        <div className="chart-title">Revenue · Cost · Profit</div>
        <div className="chart-sub" style={{ marginTop: 8 }}>Loading monthly data…</div>
      </div>
    );
  }

  const editBtn = (
    <div style={{display:"flex",gap:8}}>
      <button onClick={runInvoiceSync} disabled={syncing}
        style={{padding:"6px 14px",borderRadius:6,border:"1px solid #14213d",background:syncing?"#0f1e3a":"#070e20",color:syncing?"#6889b4":"#8fa4c8",cursor:syncing?"wait":"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}
        title="Pull paid + partially-paid invoices from Tracker now. Cron runs every 6h; this forces it.">
        {syncing ? "⟳ Syncing…" : "⟳ Sync Invoices"}
      </button>
      <button onClick={() => setEditOpen(v => !v)}
        style={{padding:"6px 14px",borderRadius:6,border:"1px solid "+(editOpen?"#3b7ffa":"#14213d"),background:editOpen?"rgba(59,127,250,.14)":"#070e20",color:editOpen?"#60a5fa":"#8fa4c8",cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>
        {editOpen ? "✕ Close Editor" : "✎ Edit Monthly Values"}
      </button>
    </div>
  );

  return (
    <>
      {/* Section header bar — single Edit toggle that opens the shared editor
          below all three charts. Previously the button lived on the Revenue
          card header only, which made it unclear that Cost + Profit values
          were editable via the same panel. */}
      <div className="chart-card" style={{padding:"14px 18px", marginBottom:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div>
            <div className="chart-title" style={{marginBottom:2}}>Revenue · Cost · Profit — {year}</div>
            <div className="chart-sub">
              Revenue Actual is 🔒 tracker-owned (invoice sync). Everything else is editable by CFO / CEO / SUPER_ADMIN. Profit is derived (Revenue − Cost).
            </div>
          </div>
          {canEdit && editBtn}
        </div>
      </div>

      {/* Row 1 — Revenue + Cost side-by-side. Collapses to single column
          if only one of them is permitted for the role. */}
      {(() => {
        const showRev = canSeeGraph?.("fin_chart_revenue") || canSeeGraph?.("revenue_graph");
        const showCost = canSeeGraph?.("fin_chart_cost") || canSeeGraph?.("cost_graph");
        const visibleCount = [showRev, showCost].filter(Boolean).length;
        if (visibleCount === 0) return null;
        return (
          <div style={{display:"grid",gridTemplateColumns:`repeat(${visibleCount}, minmax(0, 1fr))`,gap:14,marginBottom:18}}>
            {showRev && (
              <div className="chart-card" style={{marginBottom:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                  <div>
                    <div className="chart-title">Revenue — {year}</div>
                    <div className="chart-sub">
                      Actual <span style={{color:"#3b7ffa"}}>(🔒 tracker — paid + pending)</span> ·
                      Expected <span style={{color:"#94b8f0"}}>(CFO)</span> ·
                      Forecast <span style={{color:"#f59e0b"}}>(dotted)</span>
                    </div>
                  </div>
                </div>
                <TriSeriesMonthlyChart
                  actual={series.revActualTotal}
                  expected={series.revExpected}
                  forecast={series.revForecast}
                  monthLabels={MONTHS}
                  height={180}
                />
              </div>
            )}
            {showCost && (
              <div className="chart-card" style={{marginBottom:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                  <div>
                    <div className="chart-title">Cost — {year}</div>
                    <div className="chart-sub">
                      Actual <span style={{color:"#ef4444"}}>(CFO)</span> ·
                      Expected <span style={{color:"#fca5a5"}}>(CFO)</span> ·
                      Forecast <span style={{color:"#f59e0b"}}>(dotted)</span>
                    </div>
                  </div>
                </div>
                <TriSeriesMonthlyChart
                  actual={series.costActual}
                  expected={series.costExpected}
                  forecast={series.costForecast}
                  monthLabels={MONTHS}
                  actualColor="#ef4444"
                  expectedColor="#fca5a5"
                  height={180}
                />
              </div>
            )}
          </div>
        );
      })()}

      {/* Row 2 — Profit on its own full-width row so the derived numbers
          have enough horizontal room to show labels clearly. */}
      {(canSeeGraph?.("fin_chart_profit") || canSeeGraph?.("profit_graph")) && (
        <div className="chart-card">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
            <div>
              <div className="chart-title">Profit — {year}</div>
              <div className="chart-sub">
                Revenue − Cost per series · derived on read so it never drifts.
              </div>
            </div>
          </div>
          <TriSeriesMonthlyChart
            actual={series.proActual}
            expected={series.proExpected}
            forecast={series.proForecast}
            monthLabels={MONTHS}
            actualColor="#10c981"
            expectedColor="#86efac"
            height={180}
          />
        </div>
      )}

      {/* Shared editor panel — covers all editable cells. revenueActual is
          rendered read-only so the CFO can't stomp tracker's roll-up. */}
      {editOpen && (
        <div className="chart-card">
          <div className="chart-title">Edit monthly — {year}</div>
          <div className="chart-sub" style={{marginBottom: 10}}>
            Revenue <strong>Paid</strong> and <strong>Pending</strong> are usually overwritten by the 6h invoice sync, but editable here so you can fill months tracker can't cover — e.g. January under postpaid billing. If tracker later sends a matching invoice, the next sync replaces your value.
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,fontFamily:"'Noto Sans',sans-serif"}}>
              <thead>
                <tr style={{color:"#6889b4",textAlign:"left"}}>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}}>Month</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}} title="Tracker writes this on sync (paid + partially_paid invoices). Editable for postpaid gaps; next sync may overwrite.">Rev Paid ⟳</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}} title="Tracker writes this on sync (sent / viewed invoices). Editable for postpaid gaps; next sync may overwrite.">Rev Pending ⟳</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}}>Rev Expected</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}}>Rev Forecast</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}}>Cost Actual</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}}>Cost Expected</th>
                  <th style={{padding:"6px 4px",borderBottom:"1px solid #14213d"}}>Cost Forecast</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((m, i) => {
                  const month = i + 1;
                  const inp = {width:100,padding:"4px 6px",background:"#070e20",border:"1px solid #14213d",borderRadius:4,color:"#e2e8f6",fontSize:11};
                  const roInp = {...inp,background:"#0a1528",color:"#6889b4",border:"1px dashed #14213d"};
                  const fmt = (v) => v === null || v === undefined ? "" : Math.round(v);
                  const fieldCell = (field, seriesArr) => {
                    const saving = savingKey === `${month}|${field}`;
                    return (
                      <td style={{padding:"5px 4px"}}>
                        <input
                          type="number"
                          defaultValue={fmt(seriesArr[i])}
                          disabled={saving}
                          onBlur={(e) => {
                            const current = seriesArr[i];
                            const next = e.target.value === "" ? null : Number(e.target.value);
                            if (next === current) return;
                            saveCell(month, field, e.target.value);
                          }}
                          style={inp}
                        />
                      </td>
                    );
                  };
                  return (
                    <tr key={m} style={{borderBottom:"1px solid #0a1628"}}>
                      <td style={{padding:"5px 4px",color:"#8fa4c8",fontWeight:600}}>{m}</td>
                      {fieldCell("revenueActual",        series.revActualPaid)}
                      {fieldCell("revenueActualPending", series.revActualPending)}
                      {fieldCell("revenueExpected",      series.revExpected)}
                      {fieldCell("revenueForecast",      series.revForecast)}
                      {fieldCell("costActual",           series.costActual)}
                      {fieldCell("costExpected",         series.costExpected)}
                      {fieldCell("costForecast",         series.costForecast)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function RevenueCostWaterfall({data=[]}){
  const W=1400, H=520;
  const padL=80, padR=30, padT=50, padB=70;
  const plotW=W-padL-padR, plotH=H-padT-padB;

  // Build bridge steps: for each month, revenue lifts running, then cost drops it
  let running=0;
  const months=data.map(d=>{
    const afterRev=running+d.revenue;
    const afterCost=afterRev-d.costs;
    const step={
      m:d.m,
      revenue:d.revenue,
      costs:d.costs,
      net:d.revenue-d.costs,
      revStart:running,
      revEnd:afterRev,
      costStart:afterRev,
      costEnd:afterCost,
    };
    running=afterCost;
    return step;
  });
  const total=running;

  // Y range — revenue always positive (above zero), costs always negative (below zero)
  const maxRev=Math.max(...months.map(m=>m.revenue),1);
  const maxCost=Math.max(...months.map(m=>m.costs),1);
  const rangePad=Math.max(maxRev,maxCost)*0.14||100000;
  const yMin=-(maxCost+rangePad);
  const yMax=maxRev+rangePad;
  const y=v=>padT+plotH-((v-yMin)/(yMax-yMin))*plotH;

  const tickStep=500000;
  const yTicks=[];
  for(let t=Math.ceil(yMin/tickStep)*tickStep;t<=yMax;t+=tickStep) yTicks.push(t);

  const columns=months.length+1;
  const colW=plotW/columns;
  const subBarW=colW*0.36;
  const gap=colW*0.04;
  const fmtK=v=>{const a=Math.abs(v);return (v<0?"-":"")+(a>=1000?Math.round(a/1000)+"K kr":Math.round(a)+" kr");};

  const C_REV="#10c981", C_COST="#ef4444", C_TOTAL="#3b7ffa";
  const zeroY=y(0);

  return (
    <div style={{width:"100%",overflowX:"auto"}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",minWidth:1100,display:"block"}}>
        {yTicks.map(t=>(
          <g key={t}>
            <line x1={padL} x2={W-padR} y1={y(t)} y2={y(t)} stroke="#14213d" strokeWidth="1"/>
            <text x={padL-8} y={y(t)+4} fill="#6889b4" fontSize="11" textAnchor="end" fontFamily="'Noto Sans',sans-serif">{t.toLocaleString()}</text>
          </g>
        ))}
        {/* Zero line */}
        <line x1={padL} x2={W-padR} y1={zeroY} y2={zeroY} stroke="#6889b4" strokeWidth="1.5" strokeDasharray="4,4"/>

        {months.map((s,i)=>{
          const cx=padL+i*colW+colW/2;
          // Revenue bar: ALWAYS from zero upward (positive value)
          const revX=cx-subBarW-gap/2;
          const revTop=y(s.revenue);
          const revBot=zeroY;
          const revH=Math.max(2,revBot-revTop);
          // Cost bar: ALWAYS from zero downward (negative direction)
          const costX=cx+gap/2;
          const costTop=zeroY;
          const costBot=y(-s.costs);
          const costH=Math.max(2,costBot-costTop);
          return (
            <g key={s.m}>
              {/* Revenue (green, always above zero) */}
              <rect x={revX} y={revTop} width={subBarW} height={revH} fill={C_REV} rx="2" opacity="0.92"/>
              {/* Cost (red, always below zero) */}
              <rect x={costX} y={costTop} width={subBarW} height={costH} fill={C_COST} rx="2" opacity="0.92"/>
              {/* Value labels */}
              <text x={revX+subBarW/2} y={revTop-4} fill={C_REV} fontSize="9.5" textAnchor="middle" fontWeight="700" fontFamily="'Noto Sans',sans-serif">+{fmtK(s.revenue)}</text>
              <text x={costX+subBarW/2} y={costBot+12} fill={C_COST} fontSize="9.5" textAnchor="middle" fontWeight="700" fontFamily="'Noto Sans',sans-serif">-{fmtK(s.costs)}</text>
              {/* Month label and net */}
              <text x={cx} y={H-padB+18} fill="#8fa4c8" fontSize="12" textAnchor="middle" fontWeight="600" fontFamily="'Noto Sans',sans-serif">{s.m.slice(0,3)}</text>
              <text x={cx} y={H-padB+33} fill={s.net>=0?C_REV:C_COST} fontSize="10" textAnchor="middle" fontWeight="700" fontFamily="'Noto Sans',sans-serif">Net {(s.net>=0?"+":"")}{fmtK(s.net)}</text>
            </g>
          );
        })}

        {/* Final FY Net total bar — cumulative sum of (revenue - costs) */}
        {(()=>{
          const i=months.length;
          const cx=padL+i*colW+colW/2;
          const barW=subBarW*2+gap;
          const x=cx-barW/2;
          const yT=y(Math.max(0,total));
          const yB=y(Math.min(0,total));
          const h=Math.max(2,yB-yT);
          return (
            <g>
              <rect x={x} y={yT} width={barW} height={h} fill={C_TOTAL} rx="2"/>
              <text x={cx} y={(total>=0?yT-6:yB+14)} fill={C_TOTAL} fontSize="12" textAnchor="middle" fontWeight="800" fontFamily="'Noto Sans',sans-serif">{(total>=0?"+":"")}{fmtK(total)}</text>
              <text x={cx} y={H-padB+18} fill="#60a5fa" fontSize="12" textAnchor="middle" fontWeight="700" fontFamily="'Noto Sans',sans-serif">FY Net</text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

export default function FinancialStatusPage({ projects, scoping = [], leads = [], consultants = [], monthlyReport, setMonthlyReport, showToast, addNotification, currentUser, ChipComponent }) {
  CURRENT_USER = currentUser || CURRENT_USER;
  const Chip = ChipComponent || LegacyChip;
  const { isSuperAdmin, canSeeGraph } = useAuth();
  useChartCSS();

  const [view, setView] = useState("month");
  const [rateSort, setRateSort] = useState("rate");
  const [activeSection, setActiveSection] = useState("financial"); // financial | report
  const [editPanel, setEditPanel] = useState(null); // null | "billing" | "waterfall"

  // ── Financial Status (manual entries: revenue, costs, billing) ──
  const [finStatusData, setFinStatusData] = useState(null); // null = loading, [] = no data
  useEffect(() => {
    apiClient.getFinancialStatus(2026)
      .then(data => setFinStatusData(data))
      .catch(() => setFinStatusData([]));
  }, []);

  // codex.md Financial Board — actual / expected / forecast series per month
  // for revenue + cost. Profit is derived on the server too, but we
  // recompute locally when the CFO edits Expected/Forecast so the preview
  // is instant (no round-trip on every keystroke).
  const [monthlyFin, setMonthlyFin] = useState(null); // null = loading
  const monthlyYear = 2026;
  const reloadMonthlyFin = useCallback(() => {
    apiClient.getMonthlyFinancials(monthlyYear)
      .then((rows) => setMonthlyFin(Array.isArray(rows) ? rows : []))
      .catch(() => setMonthlyFin([]));
  }, [monthlyYear]);
  useEffect(() => { reloadMonthlyFin(); }, [reloadMonthlyFin]);

  // codex.md Phase 6 — Headcount per month, bucketed.
  const headcountSeries = useMemo(() => {
    const fulltime = Array(12).fill(0);
    const talentHub = Array(12).fill(0);
    const nonBillables = Array(12).fill(0);
    for (const c of (consultants || [])) {
      const nonBill = isNonBillableConsultant(c);
      const joinMs = c.joinDate ? new Date(c.joinDate).getTime() : 0;
      if (!isFinite(joinMs)) continue;
      const inactiveMs = c.inactiveSince ? new Date(c.inactiveSince).getTime() : null;
      for (let m = 0; m < 12; m++) {
        const monthEnd = new Date(monthlyYear, m + 1, 0, 23, 59, 59).getTime();
        if (joinMs > monthEnd) continue;
        // Exclude from the month once the inactiveSince cutoff has passed
        // the month's start — matches the backend tracker-query semantics
        // of "time entries on or after inactive_from are dropped".
        const monthStart = new Date(monthlyYear, m, 1).getTime();
        if (inactiveMs !== null && inactiveMs <= monthStart) continue;
        if (nonBill) nonBillables[m]++;
        else if (c.employmentType === "FULL_TIME") fulltime[m]++;
        else if (c.employmentType === "TALENT_HUB") talentHub[m]++;
      }
    }
    return { fulltime, talentHub, nonBillables };
  }, [consultants, monthlyYear]);

  // codex.md Row 1 KPIs — Revenue / Cost / Profit YTD from the new monthly
  // series (revenueActual = tracker invoice sync, costActual = CFO manual).
  // Replaces the legacy "tracker revenueTrend OR FinancialStatusEntry
  // legacy revenue/costs" heuristic. Profit derived on read.
  const monthlyYtd = useMemo(() => {
    if (!Array.isArray(monthlyFin)) return null;
    const now = new Date();
    const cutoff = now.getFullYear() === monthlyYear ? now.getMonth() + 1 : 12;
    const slice = monthlyFin.filter((r) => r.month <= cutoff);
    const sum = (arr) => arr.reduce((s, v) => s + (typeof v === "number" ? v : 0), 0);
    // Revenue actual on the chart = paid (cash in) + pending (billed, in
    // AR). Postpaid billing means April invoices → March's work month, so
    // the "Actual" bar for March includes both already-paid and
    // still-pending invoices.
    const revPaids    = slice.map((r) => r.revenue?.actual        ?? null).filter((v) => v !== null);
    const revPendings = slice.map((r) => r.revenue?.actualPending ?? null).filter((v) => v !== null);
    const costActuals = slice.map((r) => r.cost?.actual           ?? null).filter((v) => v !== null);
    const revenuePaid    = revPaids.length    ? sum(revPaids)    : null;
    const revenuePending = revPendings.length ? sum(revPendings) : null;
    const revenue = (revenuePaid ?? 0) + (revenuePending ?? 0) || (revenuePaid === null && revenuePending === null ? null : 0);
    const cost    = costActuals.length ? sum(costActuals) : null;
    const profit  = revenue !== null && cost !== null ? revenue - cost : null;
    const margin  = revenue !== null && revenue > 0 && profit !== null
      ? Math.round((profit / revenue) * 100) : null;
    // MoM: last two months with actuals, pairwise. Uses the combined
    // paid + pending total so the delta matches what's displayed.
    const lastIdx = slice.length - 1;
    const prevIdx = lastIdx - 1;
    const last = slice[lastIdx], prev = slice[prevIdx];
    const totalActual = (r) => (r?.revenue?.actual ?? 0) + (r?.revenue?.actualPending ?? 0);
    const mom = (last && prev) ? {
      revenue: totalActual(last) - totalActual(prev),
      costs:   (last.cost?.actual    ?? 0) - (prev.cost?.actual    ?? 0),
      profit:  (totalActual(last) - (last.cost?.actual ?? 0))
             - (totalActual(prev) - (prev.cost?.actual ?? 0)),
      prevMonth: MONTHS[prev.month - 1],
      lastMonth: MONTHS[last.month - 1],
    } : null;
    return {
      revenue, revenuePaid, revenuePending,
      cost, profit, margin, mom,
      monthsWithRevenue: Math.max(revPaids.length, revPendings.length),
      monthsWithCost:    costActuals.length,
      lastMonthName: last ? MONTHS[last.month - 1] : null,
    };
  }, [monthlyFin, monthlyYear]);

  // ── Annual targets (persisted to DB, editable inline) ──
  const [targets, setTargets] = useState({ utilisationTarget: 85, avgHourlyRateTarget: 175, billablesTarget: 100 });
  const [targetEditing, setTargetEditing] = useState(null); // "util" | "rate" | "billables"
  const [targetDraft, setTargetDraft] = useState("");

  useEffect(() => {
    apiClient.getFinancialTargets(2026)
      .then(data => setTargets({ utilisationTarget: data.utilisationTarget, avgHourlyRateTarget: data.avgHourlyRateTarget, billablesTarget: data.billablesTarget }))
      .catch(() => {});
  }, []);

  // ── Tracker utilisation data (YTD pull from RainTrack) ──
  const [trackerUtil, setTrackerUtil] = useState(null); // null=loading, false=error, object=data
  const [trackerUtilError, setTrackerUtilError] = useState("");
  const [trackerUtilSyncing, setTrackerUtilSyncing] = useState(false);
  // codex.md: manual ⟳ Sync Utilisation button. Re-runs the tracker
  // fetch + heatmap fetch without a page reload so a PM who just
  // flipped someone to RESIGNED or fixed an admin flag sees the
  // denominator refresh immediately.
  const refreshTrackerUtil = useCallback(async () => {
    setTrackerUtilSyncing(true);
    try {
      const [util, heatmap] = await Promise.all([
        apiClient.getTrackerUtilisation({
          from: `${monthlyYear}-01-01`,
          to: new Date().toISOString().slice(0, 10),
        }).catch((err) => {
          setTrackerUtilError(err?.response?.data?.error || err?.message || "Tracker fetch failed");
          return false;
        }),
        apiClient.getUtilisationHeatmap(monthlyYear).catch(() => []),
      ]);
      setTrackerUtil(util);
      if (util) setTrackerUtilError("");
      setHeatmapApi(Array.isArray(heatmap) ? heatmap : []);
    } finally {
      setTrackerUtilSyncing(false);
    }
  }, [monthlyYear]);
  useEffect(() => { refreshTrackerUtil(); }, [refreshTrackerUtil]);

  // ── NPS data (from local DB — synced from Monday, no live Monday dependency) ──
  const [wbrData, setWbrData] = useState(null);
  useEffect(() => {
    apiClient.getNpsSummary().then(setWbrData).catch(() => {});
  }, []);

  // Map tracker monthlyHours (labelled Jan/Feb/…) → aligned 12-slot actual array for the chart
  const trackerMonthlyActual = useMemo(() => {
    if (!trackerUtil?.monthly) return null;
    const short = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const byMonth = new Map(trackerUtil.monthly.map(m => [String(m.month).slice(0,3), m.utilPct]));
    return short.map(lbl => (byMonth.has(lbl) ? byMonth.get(lbl) : null));
  }, [trackerUtil]);

  const TARGET_KEY = { util: "utilisationTarget", billables: "billablesTarget", rate: "avgHourlyRateTarget" };

  async function saveTarget(field, value) {
    if (!isSuperAdmin) return;
    const key = TARGET_KEY[field];
    if (!key) return;
    const num = parseFloat(value);
    if (!Number.isFinite(num) || num <= 0) return;
    const next = { ...targets, [key]: num };
    setTargets(next);
    setTargetEditing(null);
    try {
      await apiClient.upsertFinancialTargets({ year: 2026, utilisationTarget: next.utilisationTarget, avgHourlyRateTarget: next.avgHourlyRateTarget, billablesTarget: next.billablesTarget });
    } catch {
      showToast("Failed to save target", "error");
    }
  }

  const btnStyle = (active) => ({
    padding: "6px 14px", borderRadius: 6, border: `1px solid ${active ? "#3b7ffa" : "#14213d"}`,
    background: active ? "rgba(59,127,250,.14)" : "#070e20", color: active ? "#60a5fa" : "#8fa4c8",
    cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'Noto Sans',sans-serif", whiteSpace: "nowrap",
  });

  function TargetEditRow({ field, label, value, suffix }) {
    if (targetEditing !== field) return null;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0 4px", padding: "10px 14px", background: "#070e20", border: "1px solid #3b7ffa33", borderRadius: 8 }}>
        <span style={{ fontSize: 12, color: "#8fa4c8", fontWeight: 600 }}>{label}</span>
        <input
          autoFocus
          type="number"
          value={targetDraft}
          onChange={e => setTargetDraft(e.target.value)}
          onBlur={() => saveTarget(field, targetDraft)}
          onKeyDown={e => { if (e.key === "Enter") saveTarget(field, targetDraft); if (e.key === "Escape") setTargetEditing(null); }}
          style={{ width: 100, padding: "5px 8px", background: "#0a1528", border: "1px solid #3b7ffa", borderRadius: 6, color: "#dce8f8", fontSize: 13, fontFamily: "'Noto Sans',sans-serif", outline: "none" }}
        />
        <span style={{ fontSize: 12, color: "#8fa4c8" }}>{suffix}</span>
        <span style={{ fontSize: 11, color: "#4a6a96", marginLeft: 4 }}>Press Enter or click away to save</span>
      </div>
    );
  }

  // ── Derived values from live data ──
  const derivedContract  = useMemo(()=>deriveMonthlyContract(projects),[projects]);
  const derivedForecast  = useMemo(()=>deriveForecast50(scoping,leads),[scoping,leads]);

  // ── Editable overrides (null = use derived/seed) ──
  const [billingContractOvr,  setBillingContractOvr]  = useState(()=>new Array(12).fill(null));
  const [billingForecastOvr,  setBillingForecastOvr]  = useState(()=>new Array(12).fill(null));
  const [billingActual,       setBillingActual]        = useState(()=>[...BILLING_ACTUAL_SEED]);
  const [billingTarget,       setBillingTarget]        = useState(()=>[...BILLING_TARGET_SEED]);
  const [breakEven,           setBreakEven]            = useState(BREAK_EVEN_SEED);
  const [revenueOvr,          setRevenueOvr]           = useState(()=>new Array(12).fill(null));
  const [costs,               setCosts]                = useState(()=>[...COSTS_SEED]);

  // ── Populate billing actuals from tracker invoices when available ──
  useEffect(() => {
    if (!trackerUtil?.monthlyBilling?.length) return;
    const short = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const byMonth = new Map(trackerUtil.monthlyBilling.map(b => [b.month, b]));
    const next = short.map((lbl, i) => {
      const tb = byMonth.get(lbl);
      return tb && tb.billed > 0 ? tb.billed : BILLING_ACTUAL_SEED[i] || 0;
    });
    setBillingActual(next);
  }, [trackerUtil]);

  // codex.md v3: Monthly Billing now comes from Monday board 5087548237
  // via the MondayBillingSnapshot table. Falls back to the derived /
  // seed / manual override pattern for months that Monday hasn't
  // populated yet. `hasData` flag per month tells us whether to trust
  // Monday's zero or fall back.
  const [mondayBilling, setMondayBilling] = useState(null); // null = loading
  const reloadMondayBilling = useCallback(() => {
    apiClient.getMonthlyBillingMonday(monthlyYear)
      .then((rows) => setMondayBilling(Array.isArray(rows) ? rows : []))
      .catch(() => setMondayBilling([]));
  }, [monthlyYear]);
  useEffect(() => { reloadMondayBilling(); }, [reloadMondayBilling]);

  // codex.md v4 — heatmap feed. Endpoint returns Nivo shape already;
  // the adapter is mostly a null-safety pass.
  // heatmapApi is populated by refreshTrackerUtil above — both the
  // ⟳ Sync Utilisation button and the initial mount effect feed it.
  const [heatmapApi, setHeatmapApi] = useState(null);

  // codex.md — Top Clients: fetch from the new dedicated endpoint.
  // null while loading → card renders a skeleton. Empty array once
  // loaded → genuine empty state.
  const [topClientsApi, setTopClientsApi] = useState(null);
  useEffect(() => {
    apiClient.getTopClients(monthlyYear, 5)
      .then((rows) => setTopClientsApi(Array.isArray(rows) ? rows : []))
      .catch(() => setTopClientsApi([]));
  }, [monthlyYear]);

  // ── Resolved billing data ──
  const billingData = useMemo(()=>MONTHS_IDX.map((m,i)=>{
    const mb = Array.isArray(mondayBilling) ? mondayBilling.find((r) => r.month === i + 1) : null;
    // Monday is authoritative when hasData is true. Otherwise fall back to
    // the derived / manual / seed chain that predates this integration.
    const mondayActive = mb && mb.hasData;
    return {
      m,
      contract:  mondayActive ? mb.expected : (billingContractOvr[i]!=null ? billingContractOvr[i] : derivedContract[i]),
      forecast:  mondayActive ? mb.forecast : (billingForecastOvr[i]!=null ? billingForecastOvr[i] : derivedForecast[i]),
      actual:    mondayActive ? mb.actual   : billingActual[i],
      target:    billingTarget[i],
      mondaySourced: Boolean(mondayActive),
    };
  }),[mondayBilling,billingContractOvr,billingForecastOvr,billingActual,billingTarget,derivedContract,derivedForecast]);

  // ── Tracker monthly revenue/cost lookup (falls back when no entry for a month) ──
  const trackerRevByMonth = useMemo(() => {
    if (!trackerUtil?.revenueTrend) return null;
    const map = new Map();
    trackerUtil.revenueTrend.forEach(r => map.set(String(r.month).slice(0,3), r));
    return map;
  }, [trackerUtil]);

  // ── YTD billables / costs / profit + extrapolated forecast from tracker (tracker returns rolling 6 months including prior year) ──
  const trackerYtd = useMemo(() => {
    if (!trackerUtil?.revenueTrend) return { ytdRevenue: null, ytdCosts: null, ytdProfit: null, ytdMargin: null, fyForecast: null, monthsElapsed: 0, lastMonthName: null, momDelta: null };
    const janIdx = trackerUtil.revenueTrend.findIndex(r => String(r.month).slice(0,3) === "Jan");
    if (janIdx === -1) return { ytdRevenue: null, ytdCosts: null, ytdProfit: null, ytdMargin: null, fyForecast: null, monthsElapsed: 0, lastMonthName: null, momDelta: null };
    const yearMonths = trackerUtil.revenueTrend.slice(janIdx);
    const ytdRevenue = yearMonths.reduce((s, r) => s + (r.revenue || 0), 0);
    const ytdCosts   = yearMonths.reduce((s, r) => s + (r.costs   || 0), 0);
    const ytdProfit  = ytdRevenue - ytdCosts;
    const ytdMargin  = ytdRevenue > 0 ? Math.round((ytdProfit / ytdRevenue) * 100) : null;
    const monthsElapsed = yearMonths.length;
    const fyForecast = monthsElapsed > 0 ? Math.round(ytdRevenue * 12 / monthsElapsed) : null;
    const lastMonthName = yearMonths[yearMonths.length - 1]?.month ?? null;
    const prev = yearMonths[yearMonths.length - 2];
    const last = yearMonths[yearMonths.length - 1];
    const momDelta = (prev && last) ? {
      revenue: last.revenue - prev.revenue,
      costs:   last.costs   - prev.costs,
      profit:  (last.revenue - last.costs) - (prev.revenue - prev.costs),
      prevMonth: prev.month,
      lastMonth: last.month,
    } : null;
    return { ytdRevenue, ytdCosts, ytdProfit, ytdMargin, fyForecast, monthsElapsed, lastMonthName, momDelta };
  }, [trackerUtil]);

  // ── Resolved waterfall data: tracker → manual override → seed → billing-derived ──
  const waterfallData = useMemo(()=>MONTHS_IDX.map((m,i)=>{
    const shortMonth = m.slice(0,3);
    const trackerRow = trackerRevByMonth?.get(shortMonth);
    const defaultRev = billingData[i].contract + billingData[i].forecast + billingData[i].actual;
    const trackerRev = trackerRow && trackerRow.revenue > 0 ? trackerRow.revenue : null;
    const trackerCost = trackerRow && trackerRow.costs > 0 ? trackerRow.costs : null;
    return {
      m,
      revenue: revenueOvr[i]!=null ? revenueOvr[i] : (trackerRev ?? REVENUE_SEED[i] ?? defaultRev),
      costs:   trackerCost ?? costs[i],
    };
  }),[billingData,revenueOvr,costs,trackerRevByMonth]);

  const D = FINANCIAL_DATA;
  const ED = EXTENDED_FIN_DATA;

  // ── YTD Revenue / Costs / Profit from FinancialStatusEntry ──
  // Only count months that have been entered (id !== null means a real DB entry exists)
  const finEntries = useMemo(() => {
    if (!finStatusData) return null; // still loading
    return finStatusData.filter(e => e.id !== null);
  }, [finStatusData]);

  const ytdRevenue = useMemo(() => finEntries && finEntries.length > 0
    ? finEntries.reduce((s, e) => s + (e.revenue || 0), 0)
    : null, [finEntries]);

  const ytdCosts = useMemo(() => finEntries && finEntries.length > 0
    ? finEntries.reduce((s, e) => s + (e.costs || 0), 0)
    : null, [finEntries]);

  const ytdProfit = ytdRevenue !== null && ytdCosts !== null ? ytdRevenue - ytdCosts : null;
  const ytdMargin = ytdRevenue !== null && ytdRevenue > 0 && ytdProfit !== null
    ? Math.round((ytdProfit / ytdRevenue) * 100)
    : null;

  // MoM delta: compare last 2 finEntries sorted by month
  const momDelta = useMemo(() => {
    if (!finEntries || finEntries.length < 2) return null;
    const sorted = [...finEntries].sort((a, b) => a.month - b.month);
    const prev = sorted[sorted.length - 2];
    const last = sorted[sorted.length - 1];
    const prevProfit = prev.revenue - prev.costs;
    const lastProfit = last.revenue - last.costs;
    const prevMargin = prev.revenue > 0 ? Math.round((prevProfit / prev.revenue) * 100) : null;
    const lastMargin = last.revenue > 0 ? Math.round((lastProfit / last.revenue) * 100) : null;
    return {
      revenue: last.revenue - prev.revenue,
      costs: last.costs - prev.costs,
      profit: lastProfit - prevProfit,
      margin: prevMargin !== null && lastMargin !== null ? lastMargin - prevMargin : null,
      prevMonth: MONTHS[prev.month - 1],
      lastMonth: MONTHS[last.month - 1],
    };
  }, [finEntries]);

  // FY2026 Revenue Forecast (from manual billing chart data — static for now)
  const forecastTotal  = D.billables.forecast.filter(Boolean).reduce((s,v)=>s+v,0);
  const ytdBillActual  = D.billables.actual.filter(Boolean).reduce((s,v)=>s+v,0);
  const fullYearFcast  = ytdBillActual + forecastTotal; // €K

  // codex.md: "FT+PT Avg Rate" — simple average of billRate across
  // billable consultants only (FT + TalentHub, excluding admins and
  // non-billable C-suite / HR). Resigned / bench consultants stay in the
  // list because the rate card represents the team's earning profile,
  // not current utilisation. Falls back to the tracker-driven rate (which
  // is the revenue-per-hour across tracked projects) when we have no
  // consultants loaded locally; ultimately to a static 497 for display
  // continuity.
  const ftptAvgRate = useMemo(() => {
    const today = new Date();
    const pool = (consultants || []).filter(c =>
      isActiveConsultantOn(c, today) &&
      (c.employmentType === "FULL_TIME" || c.employmentType === "TALENT_HUB")
    );
    if (pool.length === 0) return null;
    const rates = pool.map(c => Number(c.billRate) || 0).filter(r => r > 0);
    if (rates.length === 0) return null;
    return Math.round(rates.reduce((s, r) => s + r, 0) / rates.length);
  }, [consultants]);
  const trackerBlendedRate = useMemo(() => {
    if (!trackerUtil?.topProjects?.length) return null;
    const totalHours = trackerUtil.topProjects.reduce((s, p) => s + (p.hours || 0), 0);
    const totalRevenue = trackerUtil.topProjects.reduce((s, p) => s + (p.revenue || 0), 0);
    return totalHours > 0 ? Math.round(totalRevenue / totalHours) : null;
  }, [trackerUtil]);
  const blendedRateDisplay = ftptAvgRate ?? trackerBlendedRate ?? 497;
  const blendedRateSource = ftptAvgRate
    ? `Avg of ${(consultants||[]).filter(c=>isActiveConsultantOn(c,new Date())&&(c.employmentType==="FULL_TIME"||c.employmentType==="TALENT_HUB")&&Number(c.billRate)>0).length} active billable consultants · includes PM & HoT · excludes CEO / CCO / CFO / COO / HR / MD & inactive`
    : (trackerBlendedRate ? "from tracker projects" : "FT: 600 · PT: 395 · simple avg");

  // Rate Trend from FINANCIAL_DATA (guarded)
  const rateActuals   = D.avgHourlyRate.actual.filter(v => v !== null);
  const rateForecasts = D.avgHourlyRate.forecast.filter(v => v !== null);
  const rateTrendDelta = rateActuals.length > 0 && rateForecasts.length > 0
    ? rateForecasts[rateForecasts.length - 1] - rateActuals[0]
    : null;

  // Quarter aggregation helper
  function toQuarters(arr) {
    return [0,1,2,3].map(q=>{
      const slice = arr.slice(q*3, q*3+3);
      const valid = slice.filter(v=>v!==null);
      return valid.length > 0 ? Math.round(valid.reduce((a,b)=>a+b,0)/valid.length) : null;
    });
  }

  const QLABELS = ["Q1","Q2","Q3","Q4"]; // eslint-disable-line no-unused-vars
  const qUtil    = { actual:toQuarters(D.utilisation.actual),  forecast:toQuarters(D.utilisation.forecast)  };
  const qBill    = { actual:toQuarters(D.billables.actual),    forecast:toQuarters(D.billables.forecast)    };
  const qRate    = { actual:toQuarters(D.avgHourlyRate.actual), forecast:toQuarters(D.avgHourlyRate.forecast) };


  // For quarter view we override the month axis inside charts — patch via wrapper
  // (Charts use MONTHS const internally; for simplicity we keep month view accurate
  //  and note the quarterly view collapses to Q1-Q4 using same axis positions)
  // We pass a labelsOverride to render Q labels when in quarter mode
  const isQuarter = view === "quarter";

  // Sort project rate table — prefer tracker's topProjects when available, fall back to hardcoded seed
  const projectRateData = useMemo(() => {
    if (trackerUtil?.topProjects?.length > 0) {
      return trackerUtil.topProjects.map(p => ({
        name: p.name,
        clientName: p.clientName,
        revenue: p.revenue,
        hours: p.hours,
        rate: p.hours > 0 ? Math.round(p.revenue / p.hours) : 0,
        util: Math.round(p.utilization),
        model: "T&M",
        status: p.status,
        trend: p.trend,
        change: p.change,
      }));
    }
    return PROJECT_RATE_DATA;
  }, [trackerUtil]);

  const sortedProjects = [...projectRateData].sort((a,b)=>{
    if(rateSort==="rate") return b.rate - a.rate;
    if(rateSort==="util") return b.util - a.util;
    return a.name.localeCompare(b.name);
  });

  // Blended rate for rate table comparisons — prefer tracker-computed, fallback to project props
  const activeProjects = projects.filter(p => p.projectStatus !== "Cancelled");
  const blendedRate = trackerBlendedRate
    || (activeProjects.length > 0
      ? Math.round(activeProjects.reduce((s, p) => s + (p.ratePerHour || 0), 0) / activeProjects.length)
      : null);

  // ytdBill alias for chart subtitles (hardcoded data)
  const ytdBill = ytdBillActual;

  // codex.md v4 — adapter-transformed datasets, memoised once per source.
  // Every chart on the page reads from one of these; no chart component
  // computes shape during render.
  const revenueRows = useMemo(() => toRevenueRows(monthlyFin), [monthlyFin]);
  const costRows = useMemo(() => toCostRows(monthlyFin), [monthlyFin]);
  const profitRows = useMemo(() => toProfitRows(monthlyFin), [monthlyFin]);
  const billingRows = useMemo(() => toBillingBarRows(billingData), [billingData]);
  const profitMarginRows = useMemo(() => toProfitMarginRows(ED?.profitMargin), [ED]);
  const utilisationRows = useMemo(
    () => toUtilisationRows(trackerMonthlyActual, D?.utilisation, targets.utilisationTarget),
    [trackerMonthlyActual, D, targets.utilisationTarget],
  );
  const hourlyRateRows = useMemo(
    () => toHourlyRateRows(D?.avgHourlyRate, targets.avgHourlyRateTarget),
    [D, targets.avgHourlyRateTarget],
  );
  const headcountRows = useMemo(() => toHeadcountRows(headcountSeries), [headcountSeries]);
  const waterfallRows = useMemo(() => toWaterfallRows(waterfallData), [waterfallData]);
  const heatmapRows = useMemo(() => toHeatmapRows(heatmapApi), [heatmapApi]);
  const pipelineRows = useMemo(() => toPipelineBars(scoping), [scoping]);
  // Top clients uses raw snapshot rows not yet exposed by the endpoint —
  // keep empty until the controller returns a /monday-billing/clients
  // breakdown; falls back gracefully with an empty-state card.
  // null → loading (null passes through the adapter as an empty list
  // but the card's loading state keys off the raw API value, not the
  // adapter output — see TopClientsChart below).
  const topClientsRows = useMemo(() => toTopClientsBars(topClientsApi), [topClientsApi]);
  // Headcount legend counts for the active month.
  const headcountActiveMonth = useMemo(() => {
    const nowMonth = new Date().getFullYear() === monthlyYear ? new Date().getMonth() : 11;
    const ft = headcountSeries.fulltime?.[nowMonth] ?? 0;
    const th = headcountSeries.talentHub?.[nowMonth] ?? 0;
    const nb = headcountSeries.nonBillables?.[nowMonth] ?? 0;
    return { fulltime: ft, talentHub: th, nonBillable: nb, month: MONTHS[nowMonth] };
  }, [headcountSeries, monthlyYear]);

  return (
    <div className="fade">
      <div className="pg-top">
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
          <div>
            <div className="pg-title">Financial Status</div>
            <div className="pg-sub">Utilisation, billables, rate performance, and monthly reporting — FY2026</div>
          </div>
          <div style={{display:"flex",gap:2,background:"#070e20",border:"1px solid #14213d",borderRadius:8,padding:3,marginTop:4}}>
            {[["financial","📊 Financials"],["report","📋 Monthly Report"]].map(([id,lbl])=>(
              <button key={id} onClick={()=>setActiveSection(id)}
                style={{padding:"7px 16px",borderRadius:6,border:"none",background:activeSection===id?"rgba(59,127,250,.14)":"none",color:activeSection===id?"#60a5fa":"#8fa4c8",cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"'Noto Sans',sans-serif",transition:"all .12s"}}>
                {lbl}{id==="report"&&monthlyReport&&<span style={{marginLeft:6,fontSize:9,background:"#f59e0b",color:"#000",borderRadius:10,padding:"1px 5px",fontWeight:700}}>ACTIVE</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="fin-page">

        {/* ── MONTHLY REPORT TAB ───────────────────────────────────────────── */}
        {activeSection==="report"&&(
          <MonthlyReportBanner report={monthlyReport} setReport={setMonthlyReport} projects={projects} showToast={showToast} addNotification={addNotification}/>
        )}

        {/* ── FINANCIAL CHARTS TAB ─────────────────────────────────────────── */}
        {activeSection==="financial"&&(<>

        {/* codex.md Row 1 — P&L from the actual/expected/forecast monthly
            series. revenue_actual = tracker invoices (sync on demand or 6h
            cron); cost_actual = CFO-manual. Profit is derived. Each card's
            sub-line says where the number came from, so there's no more
            "from tracker" label on a CFO-manual cost cell. */}
        {canSeeGraph?.("fin_kpi_pl") && (() => {
          const revYtd = monthlyYtd?.revenue ?? null;
          const revPaid    = monthlyYtd?.revenuePaid ?? null;
          const revPending = monthlyYtd?.revenuePending ?? null;
          const costYtd = monthlyYtd?.cost ?? null;
          const profit = monthlyYtd?.profit ?? null;
          const margin = monthlyYtd?.margin ?? null;
          const mom    = monthlyYtd?.mom ?? null;
          const lastMonth = monthlyYtd?.lastMonthName ?? null;
          const revMonths = monthlyYtd?.monthsWithRevenue ?? 0;
          const costMonths = monthlyYtd?.monthsWithCost ?? 0;
          const revSub = revMonths > 0
            ? `🔒 ${fmtSekK(revPaid)} paid · ${fmtSekK(revPending)} pending · Jan–${lastMonth || ""}`
            : "Click Sync Invoices to pull";
          const costSub = costMonths > 0 ? `${costMonths} mo · Jan–${lastMonth || ""} · CFO manual entries`  : "Not yet entered by CFO";
          return (
            <div className="fin-krow">
              <KpiCard
                label="Revenue (YTD)"
                value={revYtd !== null ? fmtSekK(revYtd) : "—"}
                trend={mom ? fmtSekK(Math.abs(mom.revenue)) + " vs " + mom.prevMonth : undefined}
                up={mom ? mom.revenue >= 0 : undefined}
                sub={revSub}
                accent="#10c981"
              />
              <KpiCard
                label="Costs (YTD)"
                value={costYtd !== null ? fmtSekK(costYtd) : "—"}
                trend={mom ? fmtSekK(Math.abs(mom.costs)) + " vs " + mom.prevMonth : undefined}
                up={mom ? mom.costs <= 0 : undefined}
                sub={costSub}
                accent="#f04444"
              />
              <KpiCard
                label="Profit (YTD)"
                value={profit !== null ? fmtSekK(profit) : "—"}
                trend={mom ? fmtSekK(Math.abs(mom.profit)) + " vs " + mom.prevMonth : undefined}
                up={mom ? mom.profit >= 0 : undefined}
                sub={profit !== null ? (profit >= 0 ? "Profitable YTD · derived" : "Loss YTD · derived") : "Needs revenue + cost entered"}
                accent={profit !== null ? (profit >= 0 ? "#10c981" : "#f04444") : undefined}
              />
              <KpiCard
                label="Profit Margin %"
                value={margin !== null ? `${margin}%` : "—"}
                sub={margin !== null ? (margin >= 20 ? "Healthy margin" : "Below 20% target") : "Needs revenue + cost entered"}
                accent={margin !== null ? (margin >= 20 ? "#10c981" : "#f59e0b") : undefined}
              />
            </div>
          );
        })()}

        {/* KPI Summary row — Row 2: Operational KPIs */}
        {canSeeGraph?.("fin_kpi_ops") && (
        <div className="fin-krow">
          <KpiCard
            label="FT+PT Avg Rate"
            value={`${blendedRateDisplay} kr/hr`}
            sub={blendedRateSource}
          />
          <KpiCard
            label="Avg Utilisation (YTD)"
            value={trackerUtil && trackerUtil.avgUtilPct != null ? `${trackerUtil.avgUtilPct}%` : "—"}
            sub={trackerUtil ? `${trackerUtil.contributingCount}/${trackerUtil.totalCount} contributing (>10%)` : (trackerUtilError || "Loading tracker…")}
            accent={trackerUtil && trackerUtil.avgUtilPct >= targets.utilisationTarget ? "#10c981" : trackerUtil ? "#f59e0b" : undefined}
          />
          <KpiCard
            label="YTD Billables"
            value={trackerYtd.ytdRevenue != null ? fmtSekK(trackerYtd.ytdRevenue) : "—"}
            sub={trackerYtd.monthsElapsed > 0 ? `${trackerYtd.monthsElapsed} month${trackerYtd.monthsElapsed !== 1 ? "s" : ""} · from tracker` : "Tracker data pending"}
            accent="#10c981"
          />
          <KpiCard
            label="FY2026 Revenue Forecast"
            value={trackerYtd.fyForecast != null ? fmtSekK(trackerYtd.fyForecast) : "—"}
            sub={trackerYtd.monthsElapsed > 0 ? `Extrapolated from ${trackerYtd.monthsElapsed} month${trackerYtd.monthsElapsed !== 1 ? "s" : ""} YTD` : "—"}
            accent="#10c981"
          />
        </div>
        )}

        {/* KPI Summary row — Row 3: Project Health (from tracker) */}
        {canSeeGraph?.("fin_kpi_project_health") && trackerUtil?.projectHealth && (
          <div className="fin-krow" style={{gridTemplateColumns:"repeat(6,minmax(0,1fr))"}}>
            <KpiCard label="Active Projects" value={trackerUtil.projectHealth.active} sub="Total in flight" />
            <KpiCard label="On Track" value={trackerUtil.projectHealth.onTrack} sub="Healthy" accent={C.green} />
            <KpiCard label="Watch" value={trackerUtil.projectHealth.watch} sub="Needs attention" accent="#f59e0b" />
            <KpiCard label="At Risk" value={trackerUtil.projectHealth.atRisk} sub="Significant issues" accent={C.red} />
            <KpiCard label="Blocked" value={trackerUtil.projectHealth.blocked} sub="Halted" accent={C.red} />
            <KpiCard label="Upcoming" value={trackerUtil.projectHealth.upcoming} sub="Not started" />
          </div>
        )}

        {/* Insight strip */}
        {canSeeGraph?.("fin_kpi_insights") && (
        <div className="insight-row">
          <div className="insight-card">
            <div className="insight-lbl">P&amp;L Health</div>
            {(() => {
              const profit = trackerYtd.ytdProfit !== null ? trackerYtd.ytdProfit : ytdProfit;
              const margin = trackerYtd.ytdMargin !== null ? trackerYtd.ytdMargin : ytdMargin;
              const mom    = trackerYtd.momDelta !== null ? trackerYtd.momDelta : momDelta;
              if (profit === null || profit === undefined) {
                return (
                  <>
                    <div className="insight-val" style={{color:"#8fa4c8"}}>—</div>
                    <div className="insight-sub">No P&amp;L data entered yet</div>
                  </>
                );
              }
              return (
                <>
                  <div className="insight-val" style={{color: profit >= 0 ? C.green : C.red}}>
                    {profit >= 0 ? "Profitable" : "Net Loss"}
                  </div>
                  <div className="insight-sub">
                    {fmtSekK(profit)} profit · {margin}% margin
                    {mom?.profit !== undefined ? ` · ${mom.profit >= 0 ? "▲" : "▼"} ${fmtSekK(Math.abs(mom.profit))} vs ${mom.prevMonth}` : ""}
                  </div>
                </>
              );
            })()}
          </div>
          <div className="insight-card">
            <div className="insight-lbl">Utilisation vs Target</div>
            {trackerUtil && trackerUtil.avgUtilPct != null ? (
              <>
                <div className="insight-val" style={{color: trackerUtil.avgUtilPct >= targets.utilisationTarget ? C.green : trackerUtil.avgUtilPct >= targets.utilisationTarget * 0.8 ? "#f59e0b" : C.red}}>
                  {trackerUtil.avgUtilPct}%
                </div>
                <div className="insight-sub">Target: {targets.utilisationTarget}% · {trackerUtil.avgUtilPct >= targets.utilisationTarget ? "On target" : `${(targets.utilisationTarget - trackerUtil.avgUtilPct).toFixed(1)}pt below`}</div>
              </>
            ) : (
              <>
                <div className="insight-val" style={{color:"#8fa4c8"}}>—</div>
                <div className="insight-sub">Target: {targets.utilisationTarget}% · {trackerUtilError || "Loading tracker…"}</div>
              </>
            )}
          </div>
          <div className="insight-card">
            <div className="insight-lbl">Rate Trend (Projected)</div>
            <div className="insight-val" style={{color: rateTrendDelta !== null ? C.green : "#8fa4c8"}}>
              {rateTrendDelta !== null ? `+${rateTrendDelta} kr/hr` : "—"}
            </div>
            <div className="insight-sub">
              {rateTrendDelta !== null
                ? `${rateActuals[0]} kr → ${rateForecasts[rateForecasts.length - 1]} kr · Jan to Dec`
                : "Insufficient rate data"}
            </div>
          </div>
        </div>
        )}

        {/* codex.md v4 grid — Tremor (default) + Nivo (waterfall/headcount/
            heatmap). Rows follow the final layout spec:
              Row A: Revenue | Cost | Profit  (3-col)
              Row B: Monthly Billing | Profit Margin  (2-col)
              Row C: Headcount | Utilisation Trend  (2-col)
              Row D: Utilisation Heatmap  (full width)
              Row E: Top Clients | Pipeline  (2-col)
              Row F: Avg Hourly Rate | NPS  (2-col, supplemental)
            Every chart is fed by a memoised adapter output; RBAC gating
            is inline (`canSeeGraph?.("fin_chart_*")`). */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3, minmax(0,1fr))",gap:14,marginBottom:18}}>
          <RevenueChart data={revenueRows} canSee={!!canSeeGraph?.("fin_chart_revenue")} year={monthlyYear}/>
          <CostChart    data={costRows}    canSee={!!canSeeGraph?.("fin_chart_cost")}    year={monthlyYear}/>
          <ProfitChart  data={profitRows}  canSee={!!canSeeGraph?.("fin_chart_profit")}  year={monthlyYear}/>
        </div>

        {/* Monthly P&L editor — ⟳ Sync Invoices + ✎ Edit Monthly Values.
            Only SUPER_ADMIN / CEO / CFO can invoke the sync (backend
            enforces); the table itself is visible to any role that can
            see the P&L row so they understand where the numbers come
            from, even if they can't edit. */}
        <div style={{marginBottom:18}}>
          <MonthlyFinancialsEditor
            monthlyFin={monthlyFin}
            reloadMonthlyFin={reloadMonthlyFin}
            canSee={!!canSeeGraph?.("fin_kpi_pl")}
            showToast={showToast}
            year={monthlyYear}
          />
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0,1fr))",gap:14,marginBottom:18}}>
          <MonthlyBillingChartV2
            data={billingRows}
            canSee={!!canSeeGraph?.("fin_chart_monthly_billing")}
            breakEven={breakEven}
            mondaySourced={Array.isArray(mondayBilling) && mondayBilling.some((r) => r.hasData)}
            right={
              <div style={{display:"flex",gap:8}}>
                <button onClick={async()=>{
                  try {
                    const r = await apiClient.syncMondayBillingNow();
                    const zeros = r?.zeroValueRows || 0;
                    showToast?.("Monday billing sync", `${r?.written||0} rows · ${r?.skippedUnparsed||0} unparsed${zeros ? ` · ${zeros} zero-value` : ''}`);
                    await reloadMondayBilling();
                  } catch(e){ showToast?.("Error", e?.response?.data?.error || "Sync failed"); }
                }}
                  style={{padding:"6px 12px",borderRadius:6,border:"1px solid #14213d",background:"#070e20",color:"#8fa4c8",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
                  ⟳ Sync Monday
                </button>
              </div>
            }
          />
          <ProfitMarginChartV2
            data={profitMarginRows}
            canSee={!!canSeeGraph?.("fin_chart_profit_margin")}
            target={ED.profitMargin.target}
          />
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0,1fr))",gap:14,marginBottom:18}}>
          <HeadcountChartV2
            data={headcountRows}
            canSee={!!(canSeeGraph?.("fin_chart_headcount") || canSeeGraph?.("headcount_graph"))}
            year={monthlyYear}
            activeMonthCounts={headcountActiveMonth}
          />
          <UtilisationTrendChart
            data={utilisationRows}
            canSee={!!canSeeGraph?.("fin_chart_utilisation_forecast")}
            target={targets.utilisationTarget}
            right={
              <div style={{display:"flex",gap:6}}>
                <button
                  onClick={refreshTrackerUtil}
                  disabled={trackerUtilSyncing}
                  title="Re-run the tracker utilisation pull now. Use after changing admin flags / RESIGNED status to refresh the KPI and heatmap."
                  style={{padding:"5px 10px",borderRadius:6,border:"1px solid #14213d",background:trackerUtilSyncing?"#0f1e3a":"#070e20",color:trackerUtilSyncing?"#6889b4":"#8fa4c8",cursor:trackerUtilSyncing?"wait":"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
                  {trackerUtilSyncing ? "⟳ Syncing…" : "⟳ Sync Utilisation"}
                </button>
                {isSuperAdmin && (
                  <button style={btnStyle(targetEditing==="util")}
                    onClick={()=>{ if(targetEditing==="util"){setTargetEditing(null);}else{setTargetEditing("util");setTargetDraft(String(targets.utilisationTarget));} }}>
                    {targetEditing==="util"?"✕ Close":"✎ Target"}
                  </button>
                )}
              </div>
            }
          />
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0,1fr))",gap:14,marginBottom:18}}>
          <TopClientsChart
            data={topClientsRows}
            loading={topClientsApi === null}
            canSee={!!canSeeGraph?.("fin_chart_top_clients")}
          />
          <PipelineChartV2
            data={pipelineRows}
            canSee={!!canSeeGraph?.("fin_chart_pipeline")}
          />
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0,1fr))",gap:14,marginBottom:18}}>
          <AverageHourlyRateChart
            data={hourlyRateRows}
            canSee={!!canSeeGraph?.("fin_chart_avg_hourly_rate")}
            target={targets.avgHourlyRateTarget}
          />
          <NpsChartV2
            wbrData={wbrData}
            canSee={!!canSeeGraph?.("fin_chart_nps")}
            target={ED.nps.target}
          />
        </div>

        {/* Legacy Waterfall — opt-in behind its own graph key. Reuses the
            v2 Nivo replacement for consistency. */}
        <WaterfallChartV2
          data={waterfallRows}
          canSee={!!canSeeGraph?.("fin_chart_waterfall_legacy")}
        />

        {/* Utilisation Heatmap — bottom of the page. Full width; custom
            colour scale (orange <10%, light→dark green 10–100%, red
            >100%) defined inside the component. */}
        <div style={{marginBottom:18}}>
          <UtilisationHeatmap
            data={heatmapRows}
            canSee={!!canSeeGraph?.("fin_chart_utilisation_heatmap")}
          />
        </div>


        {/* Per-Project Rate Breakdown Table — below */}

        {/* ── Project Rate Breakdown Table ─────────────────────────────── */}
        {canSeeGraph?.("fin_table_project_rate_util") && (
        <div className="chart-card" style={{marginBottom:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
            <div>
              <div className="chart-title">Per-Project Rate &amp; Utilisation Breakdown</div>
              <div className="chart-sub" style={{marginBottom:0}}>Hourly rate, utilisation, and billing model across active projects</div>
            </div>
            <div style={{display:"flex",gap:6}}>
              {[["rate","Sort by Rate"],["util","Sort by Util."],["name","Sort by Name"]].map(([k,l])=>(
                <button key={k}
                  style={{background:rateSort===k?"rgba(59,127,250,.14)":"#070e20",border:`1px solid ${rateSort===k?"rgba(59,127,250,.3)":"#14213d"}`,color:rateSort===k?"#60a5fa":"#8fa4c8",borderRadius:6,padding:"5px 11px",fontSize:11,cursor:"pointer",fontFamily:"'Noto Sans',sans-serif",transition:"all .12s"}}
                  onClick={()=>setRateSort(k)}>{l}</button>
              ))}
            </div>
          </div>
          <table className="rate-table">
            <thead>
              <tr><th>Project</th><th>Client</th><th>Model</th><th>Rate/hr</th><th style={{minWidth:140}}>Utilisation</th><th>vs Target</th></tr>
            </thead>
            <tbody>
              {sortedProjects.map((row,i)=>{
                const proj=projects.find(p=>p.projectName===row.name);
                const client=row.clientName||proj?.clientName||"—";
                const delta = blendedRate !== null ? row.rate - blendedRate : null;
                const utilDelta=row.util-targets.utilisationTarget;
                return (
                  <tr key={i}>
                    <td style={{fontWeight:600,color:"#dce8f8",maxWidth:200}}>
                      <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.name}</div>
                    </td>
                    <td style={{color:C.sub,fontSize:12}}>{client}</td>
                    <td><Chip status={row.model}/></td>
                    <td>
                      <span className="mono" style={{fontWeight:700,fontSize:14,color:blendedRate===null||row.rate>=blendedRate?C.green:C.yellow,whiteSpace:"nowrap"}}>{row.rate} kr</span>
                      {delta !== null && <span style={{fontSize:10.5,color:delta>=0?C.green:C.red,marginLeft:5,whiteSpace:"nowrap"}}>{delta>=0?"+":""}{delta} kr</span>}
                    </td>
                    <td style={{minWidth:160}}><SparkBar val={row.util} max={100} color={row.util>=80?C.green:row.util>=60?C.yellow:C.red}/></td>
                    <td><span className={utilDelta>=0?"delta-up":"delta-dn"}>{utilDelta>=0?"+":""}{utilDelta}pt</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:16,padding:"14px 0 2px",borderTop:"1px solid #14213d"}}>
            {[["Highest Rate",`${Math.max(...projectRateData.map(p=>p.rate))} kr/hr`,C.green,projectRateData.reduce((best,p)=>p.rate>best.rate?p:best,projectRateData[0]||{name:"—"}).name],["Lowest Rate",`${Math.min(...projectRateData.map(p=>p.rate))} kr/hr`,C.red,projectRateData.reduce((worst,p)=>p.rate<worst.rate?p:worst,projectRateData[0]||{name:"—"}).name],["Rate Range",`${Math.max(...projectRateData.map(p=>p.rate))-Math.min(...projectRateData.map(p=>p.rate))} kr spread`,C.blue,`${Math.min(...projectRateData.map(p=>p.rate))} – ${Math.max(...projectRateData.map(p=>p.rate))} kr/hr`]].map(([lbl,val,col,sub])=>(
              <div key={lbl} style={{padding:"10px 12px",background:"#070e20",borderRadius:8,border:"1px solid #14213d"}}>
                <div style={{fontSize:9.5,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:".1em",marginBottom:6}}>{lbl}</div>
                <div className="mono" style={{fontSize:17,fontWeight:800,color:col}}>{val}</div>
                <div style={{fontSize:11,color:C.sub,marginTop:3}}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
        )}
        </>)}
      </div>
    </div>
  );
}
// ── Quarter variants of charts (4-bar, same logic, different x-axis) ───────

function QuarterComboChart({ actual, forecast, target, unit, actualColor="#3b7ffa", forecastColor="#60a5fa" }) {
  const QL=["Q1","Q2","Q3","Q4"];
  const W=580,H=190;
  const PAD={top:20,right:60,bottom:36,left:52};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const allVals=[...actual.filter(Boolean),...forecast.filter(Boolean),target];
  const yMin=Math.max(0,Math.floor(Math.min(...allVals)*0.88/10)*10);
  const yMax=Math.ceil(Math.max(...allVals)*1.08/10)*10;
  const yRange=yMax-yMin;
  const sY=v=>PAD.top+innerH-((v-yMin)/yRange)*innerH;
  const sX=i=>PAD.left+(i/(QL.length-1))*innerW;
  const bW=(innerW/QL.length)*0.5;
  const yTicks=[]; const step=Math.ceil(yRange/4/10)*10; for(let t=yMin;t<=yMax+0.01;t+=step)yTicks.push(t);
  const tY=sY(target);
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",maxWidth:580,display:"block"}} xmlns="http://www.w3.org/2000/svg">
        {yTicks.map(t=><line key={t} x1={PAD.left} y1={sY(t).toFixed(1)} x2={PAD.left+innerW} y2={sY(t).toFixed(1)} stroke="#14213d" strokeWidth="1"/>)}
        {actual.map((v,i)=>v!==null?<rect key={`a${i}`} x={(sX(i)-bW).toFixed(1)} y={sY(v).toFixed(1)} width={(bW*0.9).toFixed(1)} height={(sY(yMin)-sY(v)).toFixed(1)} rx="3" fill={actualColor} opacity="0.9"/>:null)}
        {forecast.map((v,i)=>v!==null?<rect key={`f${i}`} x={(sX(i)+bW*0.1).toFixed(1)} y={sY(v).toFixed(1)} width={(bW*0.9).toFixed(1)} height={(sY(yMin)-sY(v)).toFixed(1)} rx="3" fill={forecastColor} opacity="0.45"/>:null)}
        <line x1={PAD.left} y1={tY.toFixed(1)} x2={PAD.left+innerW} y2={tY.toFixed(1)} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7"/>
        <text x={PAD.left+innerW+4} y={tY+4} fill="#f59e0b" fontSize="10" fontFamily="'Noto Sans Mono',monospace" opacity="0.85">target</text>
        {yTicks.map(t=><text key={t} x={PAD.left-6} y={(sY(t)+4).toFixed(1)} textAnchor="end" fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">{unit==="%"?`${t}%`:unit==="€K"?`${t}K kr`:`${t} kr`}</text>)}
        {QL.map((m,i)=><text key={i} x={sX(i).toFixed(1)} y={PAD.top+innerH+18} textAnchor="middle" fill="#8fa4c8" fontSize="12" fontFamily="'Noto Sans',sans-serif" fontWeight="600">{m}</text>)}
        <line x1={PAD.left} y1={PAD.top+innerH} x2={PAD.left+innerW} y2={PAD.top+innerH} stroke="#14213d" strokeWidth="1"/>
        {actual.map((v,i)=>v!==null?<text key={i} x={sX(i).toFixed(1)} y={(sY(v)-7).toFixed(1)} textAnchor="middle" fill={actualColor} fontSize="11" fontFamily="'Noto Sans Mono',monospace" fontWeight="600">{unit==="%"?`${v}%`:`${v}K kr`}</text>:null)}
        {forecast.map((v,i)=>v!==null?<text key={i} x={(sX(i)+bW*0.55).toFixed(1)} y={(sY(v)-7).toFixed(1)} textAnchor="middle" fill={forecastColor} fontSize="10" fontFamily="'Noto Sans Mono',monospace" opacity="0.8">{unit==="%"?`${v}%`:`${v}K kr`}</text>:null)}
      </svg>
    </div>
  );
}

function QuarterLineChart({ actual, forecast, target, unit="kr" }) {
  const QL=["Q1","Q2","Q3","Q4"];
  const W=580,H=180;
  const PAD={top:20,right:60,bottom:36,left:52};
  const innerW=W-PAD.left-PAD.right,innerH=H-PAD.top-PAD.bottom;
  const allVals=[...actual.filter(Boolean),...forecast.filter(Boolean),target];
  const yMin=Math.floor(Math.min(...allVals)*0.97/5)*5,yMax=Math.ceil(Math.max(...allVals)*1.03/5)*5,yRange=yMax-yMin||1;
  const sY=v=>PAD.top+innerH-((v-yMin)/yRange)*innerH,sX=i=>PAD.left+(i/(QL.length-1))*innerW;
  const yTicks=[]; const step=Math.ceil(yRange/3/5)*5; for(let t=yMin;t<=yMax+0.01;t+=step)yTicks.push(t);
  const aP=actual.map((v,i)=>v!==null?{x:sX(i),y:sY(v),v}:null).filter(Boolean);
  const fP=forecast.map((v,i)=>v!==null?{x:sX(i),y:sY(v),v}:null).filter(Boolean);
  const aPath=aP.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const fPath=fP.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const tY=sY(target);
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",maxWidth:580,display:"block"}} xmlns="http://www.w3.org/2000/svg">
        {yTicks.map(t=><line key={t} x1={PAD.left} y1={sY(t).toFixed(1)} x2={PAD.left+innerW} y2={sY(t).toFixed(1)} stroke="#14213d" strokeWidth="1"/>)}
        {fPath&&<path d={fPath} fill="none" stroke="#60a5fa" strokeWidth="2" strokeDasharray="5,3" opacity="0.8"/>}
        {aPath&&<path d={aPath} fill="none" stroke="#10c981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
        <line x1={PAD.left} y1={tY.toFixed(1)} x2={PAD.left+innerW} y2={tY.toFixed(1)} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7"/>
        <text x={PAD.left+innerW+4} y={tY+4} fill="#f59e0b" fontSize="10" fontFamily="'Noto Sans Mono',monospace" opacity="0.85">target</text>
        {aP.map((p,i)=><g key={i}><circle cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="5" fill="#10c981" stroke="#0a1528" strokeWidth="2"/><text x={p.x.toFixed(1)} y={(p.y-11).toFixed(1)} textAnchor="middle" fill="#10c981" fontSize="11" fontFamily="'Noto Sans Mono',monospace" fontWeight="600">{unit}{p.v}</text></g>)}
        {fP.map((p,i)=><g key={i}><circle cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="4" fill="#60a5fa" stroke="#0a1528" strokeWidth="1.5" opacity="0.8"/><text x={p.x.toFixed(1)} y={(p.y-11).toFixed(1)} textAnchor="middle" fill="#60a5fa" fontSize="11" fontFamily="'Noto Sans Mono',monospace" opacity="0.8">{unit}{p.v}</text></g>)}
        {yTicks.map(t=><text key={t} x={PAD.left-6} y={(sY(t)+4).toFixed(1)} textAnchor="end" fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">{unit}{t}</text>)}
        {QL.map((m,i)=><text key={i} x={sX(i).toFixed(1)} y={PAD.top+innerH+18} textAnchor="middle" fill="#8fa4c8" fontSize="12" fontFamily="'Noto Sans',sans-serif" fontWeight="600">{m}</text>)}
        <line x1={PAD.left} y1={PAD.top+innerH} x2={PAD.left+innerW} y2={PAD.top+innerH} stroke="#14213d" strokeWidth="1"/>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MINI BAR CHART — used for headcount & pipeline activity (stacked/grouped)
// ─────────────────────────────────────────────────────────────────────────────
function MiniGroupedBarChart({series, labels, colors, height=160}){
  const W=780,H=height;
  const PAD={top:14,right:12,bottom:28,left:36};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const allVals=series.flatMap(s=>s.data.filter(Boolean));
  if(!allVals.length) return null;
  const yMax=Math.ceil(Math.max(...allVals)*1.15/5)*5||10;
  const sY=v=>PAD.top+innerH-((v/yMax)*innerH);
  const sX=i=>PAD.left+(i/(labels.length))*innerW+(innerW/(labels.length))*0.1;
  const colW=(innerW/labels.length)*0.8/series.length;
  const yTicks=[0,Math.round(yMax/2),yMax];
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",display:"block"}} xmlns="http://www.w3.org/2000/svg">
        {yTicks.map(t=><line key={t} x1={PAD.left} y1={sY(t).toFixed(1)} x2={PAD.left+innerW} y2={sY(t).toFixed(1)} stroke="#14213d" strokeWidth="1"/>)}
        {labels.map((lbl,i)=>(
          <g key={i}>
            {series.map((s,si)=>{
              const v=s.data[i];
              if(v==null)return null;
              const x=sX(i)+si*colW;
              return <g key={si}>
                <rect x={x.toFixed(1)} y={sY(v).toFixed(1)} width={(colW*0.88).toFixed(1)} height={(sY(0)-sY(v)).toFixed(1)} rx="2" fill={colors[si]} opacity="0.85"/>
              </g>;
            })}
            <text x={(sX(i)+(colW*series.length)/2).toFixed(1)} y={PAD.top+innerH+16} textAnchor="middle" fontSize="10" fill="#8fa4c8" fontFamily="'Noto Sans',sans-serif">{lbl}</text>
          </g>
        ))}
        {yTicks.map(t=><text key={t} x={PAD.left-4} y={(sY(t)+4).toFixed(1)} textAnchor="end" fontSize="9.5" fill="#364f72" fontFamily="'Noto Sans Mono',monospace">{t}</text>)}
        <line x1={PAD.left} y1={PAD.top+innerH} x2={PAD.left+innerW} y2={PAD.top+innerH} stroke="#14213d" strokeWidth="1"/>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF PREVIEW MODAL — renders the full formatted monthly report
// ─────────────────────────────────────────────────────────────────────────────
function PDFPreviewModal({report, projects, onClose, showToast}){
  const now=new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
  const D=FINANCIAL_DATA, ED=EXTENDED_FIN_DATA;
  const totalBill=projects.reduce((s,p)=>s+(p.billables||0),0);
  const avgUtil=projects.length>0?Math.round(projects.reduce((s,p)=>s+(p.utilizationPct||0),0)/projects.length):0;
  // Effective NPS per project (override → client fallback). Treat 0 as
  // "not set" so we fall through to clientNps; filter final result to
  // exclude projects where neither source has data.
  const npsEff=projects.map(p=>{const pn=typeof p.NPS==="number"&&p.NPS>0?p.NPS:null;const cn=typeof p.clientNps==="number"&&p.clientNps>0?p.clientNps:null;return pn??cn;}).filter(v=>typeof v==="number"&&v>0);
  const avgNPS=npsEff.length>0?(npsEff.reduce((s,v)=>s+v,0)/npsEff.length).toFixed(1):"—";
  const avgP=projects.length>0?Math.round(projects.reduce((s,p)=>s+(p.profitPct||0),0)/projects.length):0;

  function handleDownload(){
    showToast("PDF exported","Monthly report saved to downloads");
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-start justify-center py-5 overflow-y-auto backdrop-blur-md" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="bg-white w-[800px] max-w-[96vw] min-h-[600px] rounded overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.5)]">
        <div className="bg-[#1e293b] px-4 py-2.5 flex items-center justify-between">
          <span className="text-[12.5px] font-semibold text-[#94a3b8]">📄 {report.monthLabel} — Monthly Report Preview</span>
          <div style={{display:"flex",gap:8}}>
            <button className="bg-white/10 border-0 text-[#94a3b8] cursor-pointer px-3 py-1 rounded-[5px] text-xs font-sans transition-colors hover:bg-white/20 hover:text-white" onClick={handleDownload}>⬇ Download PDF</button>
            <button className="bg-white/10 border-0 text-[#94a3b8] cursor-pointer px-3 py-1 rounded-[5px] text-xs font-sans transition-colors hover:bg-white/20 hover:text-white" onClick={onClose}>✕ Close</button>
          </div>
        </div>
        <div className="px-12 py-10 bg-white text-[#1e293b] font-serif">
          {/* Header */}
          <div className="flex justify-between items-start mb-7 pb-4 border-b-2 border-[#1e3a5f]">
            <div>
              <div className="text-[11px] font-bold text-[#1e3a5f] tracking-[0.15em] uppercase">
                <svg viewBox="0 0 120 28" width="110" height="26" xmlns="http://www.w3.org/2000/svg">
                  <g fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round">
                    <line x1="6" y1="4" x2="2" y2="14"/>
                    <line x1="13" y1="4" x2="9" y2="14"/>
                    <line x1="20" y1="4" x2="16" y2="14"/>
                  </g>
                  <text x="28" y="19" fontFamily="-apple-system,Segoe UI,sans-serif" fontSize="16" fontWeight="700" fill="#0f172a" letterSpacing="0.5">raincode</text>
                </svg>
              </div>
              <div style={{fontSize:9,color:"#94a3b8",marginTop:3}}>raincode.tech · Bahrain · Stockholm</div>
            </div>
            <div className="text-right">
              <div className="text-[22px] font-bold text-[#1e293b] leading-[1.2]">Monthly Reporting — {report.monthLabel}</div>
              <div className="text-xs text-[#64748b] mt-1">Generated {now} · Confidential — Management Only</div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-2.5 mb-5">
            {[["Total Billables",`$${(totalBill/1000).toFixed(0)}K`],["Avg Utilisation",`${avgUtil}%`],["Avg NPS",avgNPS],["Avg Profit Margin",`${avgP}%`]].map(([l,v])=>(
              <div key={l} className="bg-[#f8fafc] border border-[#e2e8f0] rounded px-3 py-2.5 text-center">
                <div className="text-[9px] font-bold text-[#64748b] uppercase tracking-[0.1em] mb-1">{l}</div>
                <div className="text-lg font-bold text-[#1e293b] font-[Courier_New,monospace]">{v}</div>
              </div>
            ))}
          </div>

          {/* CEO Summary */}
          <div className="mb-[26px]">
            <div className="text-[11px] font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-2.5 pb-1 border-b border-[#e2e8f0]">CEO Summary — {report.monthLabel}</div>
            <div className="text-[13px] leading-[1.8] text-[#374151]">{report.deptComments.ceo||"No executive summary provided."}</div>
          </div>

          {/* Department Comments — 2-col grid */}
          <div className="mb-[26px]">
            <div className="text-[11px] font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-2.5 pb-1 border-b border-[#e2e8f0]">Department Summaries</div>
            <div className="grid grid-cols-2 gap-4">
              {REPORT_DEPTS.map(dept=>(
                <div key={dept.id} className="bg-[#f8fafc] border border-[#e2e8f0] rounded px-4 py-3.5">
                  <div className="text-[11px] font-bold text-[#1e3a5f] mb-1.5 uppercase tracking-[0.1em]">{dept.icon} {dept.label}</div>
                  <div className="text-[12px] leading-[1.7] text-[#374151]">{report.deptComments[dept.id]||<span style={{color:"#94a3b8",fontStyle:"italic"}}>No comment submitted.</span>}</div>
                  <div style={{fontSize:10,color:"#94a3b8",marginTop:6}}>Submitted by {dept.head}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Status Table */}
          <div className="mb-[26px]">
            <div className="text-[11px] font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-2.5 pb-1 border-b border-[#e2e8f0]">Project Status</div>
            <table className="w-full border-collapse text-[12px] [&_th]:bg-[#1e3a5f] [&_th]:text-white [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-[10px] [&_th]:uppercase [&_th]:tracking-[0.08em] [&_td]:px-2.5 [&_td]:py-2 [&_td]:border-b [&_td]:border-[#e2e8f0] [&_td]:text-[#374151] hover:[&_tr]:bg-[#f8fafc]">
              <thead>
                <tr><th>Project</th><th>Client</th><th>Status</th><th>NPS</th><th>Util.</th><th>Billables</th><th>Profit</th></tr>
              </thead>
              <tbody>
                {projects.map(p=>(
                  <tr key={p.projectId}>
                    <td style={{fontWeight:600,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.projectName}</td>
                    <td>{p.clientName}</td>
                    <td><span style={{fontWeight:700,color:p.projectStatus==="On Track"?"#10c981":p.projectStatus==="Critical"?"#f04444":"#f59e0b"}}>{p.projectStatus}</span></td>
                    <td style={{textAlign:"center"}}>{(() => {
                      const pn = typeof p.NPS === "number" && p.NPS > 0 ? p.NPS : null;
                      const cn = typeof p.clientNps === "number" && p.clientNps > 0 ? p.clientNps : null;
                      return pn ?? cn ?? "—";
                    })()}</td>
                    <td style={{textAlign:"center"}}>{p.utilizationPct}%</td>
                    <td>${(p.billables/1000).toFixed(0)}K</td>
                    <td>{p.profitPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Financial Summary */}
          <div className="mb-[26px]">
            <div className="text-[11px] font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-2.5 pb-1 border-b border-[#e2e8f0]">Financial Summary</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[["YTD Billables",`${D.billables.actual.filter(Boolean).reduce((a,b)=>a+b,0)}K kr`,`vs ${D.billables.target*3}K kr target`],["FY2026 Forecast",`${D.billables.actual.filter(Boolean).reduce((a,b)=>a+b,0)+D.billables.forecast.filter(Boolean).reduce((a,b)=>a+b,0)}K kr`,"Full year projection"],["Avg Hourly Rate",`${D.avgHourlyRate.actual.filter(Boolean).slice(-1)[0]} kr/hr`,"Blended across T&M"],["Headcount",`${(ED.headcount.employees.filter(Boolean).slice(-1)[0]||0)+(ED.headcount.contractors.filter(Boolean).slice(-1)[0]||0)} total`,"Employees + contractors"]].map(([l,v,s])=>(
                <div key={l} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:6,padding:"10px 12px"}}>
                  <div style={{fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>{l}</div>
                  <div style={{fontSize:17,fontWeight:700,color:"#1e293b",fontFamily:"'Courier New',monospace"}}>{v}</div>
                  <div style={{fontSize:10.5,color:"#94a3b8",marginTop:2}}>{s}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 pt-3 border-t border-[#e2e8f0] flex justify-between text-[10px] text-[#94a3b8]">
            <span>The Situation Room — {report.monthLabel} Monthly Report</span>
            <span>Initiated by {report.initiatedBy} · {report.initiatedAt}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Extracted dept card — useState can't be called inside .map() callback
function DeptCard({dept, report, submitDept}){
  const isMe=dept.head===CURRENT_USER.name;
  const hasText=!!(report.deptComments[dept.id]&&report.deptComments[dept.id].trim());
  const [localText,setLocal]=useState(report.deptComments[dept.id]||"");
  return (
    <div data-submitted={hasText} data-mine={isMe} className="bg-[#070e20] border border-rc-border rounded-[9px] px-3.5 py-3 transition-colors data-[submitted=true]:border-[rgba(16,201,129,0.3)] data-[submitted=true]:bg-[rgba(16,201,129,0.05)] data-[mine=true]:border-[rgba(59,127,250,0.3)]">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:7}}>
        <div>
          <div className="text-[12.5px] font-bold text-rc-text-primary mb-0.5">{dept.icon} {dept.label}</div>
          <div className="text-[11px] text-[#364f72] mb-2.5">{dept.head}</div>
        </div>
        <div className="flex items-center gap-1 text-[11.5px] font-bold" style={{color:hasText?C.green:C.dim}}>
          {hasText?<><span>✓</span><span style={{fontSize:10}}>Done</span></>:<span style={{fontSize:10}}>Pending</span>}
        </div>
      </div>
      {(isMe||hasText)?(
        <>
          <textarea className="w-full bg-rc-surface border border-[#1c2e52] rounded-[7px] px-3 py-2.5 text-[12px] text-rc-text-primary font-sans resize-y outline-none min-h-[90px] leading-[1.6] transition-colors focus:border-rc-accent placeholder:text-[#253550]" rows={3} placeholder={dept.placeholder} value={localText}
            onChange={e=>setLocal(e.target.value)} readOnly={hasText&&!isMe}/>
          {isMe&&(
            <button className="w-full mt-2 bg-[rgba(59,127,250,0.14)] border border-[rgba(59,127,250,0.28)] text-rc-accent-strong rounded-[7px] px-3.5 py-1.5 text-[12px] font-semibold cursor-pointer font-sans transition-colors hover:bg-[rgba(59,127,250,0.24)] disabled:opacity-40 disabled:cursor-not-allowed" disabled={!localText.trim()||hasText}
              onClick={()=>submitDept(dept.id,localText)}>
              {hasText?"✓ Submitted":"Submit Comment"}
            </button>
          )}
        </>
      ):(
        <div style={{fontSize:12,color:C.dim,fontStyle:"italic",marginTop:4}}>Waiting for {dept.head} to submit…</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MONTHLY REPORT WORKFLOW BANNER — embedded in Financial Status page
// ─────────────────────────────────────────────────────────────────────────────
function MonthlyReportBanner({report, setReport, projects, showToast, addNotification}){
  const [ceoText,setCeoText]=useState(report?.deptComments?.ceo||"");
  const [showPreview,setShowPreview]=useState(false);

  if(!report){
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(59,127,250,.06)",border:"1px solid rgba(59,127,250,.18)",borderRadius:10,padding:"14px 20px",marginBottom:22}}>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:C.text}}>Monthly Report — March 2026</div>
          <div style={{fontSize:12,color:C.sub,marginTop:2}}>Initiate the monthly report. Dept. heads will be prompted to submit their comments before you generate the final PDF.</div>
        </div>
        <button className="inline-flex items-center gap-2 bg-[rgba(64,117,193,0.1)] border border-[rgba(64,117,193,0.3)] text-rc-blue-500 rounded-[9px] px-5 py-2.5 text-[13.5px] font-bold cursor-pointer transition-colors hover:bg-[rgba(64,117,193,0.18)]" onClick={()=>{
          const newReport={
            id:uid(), monthLabel:"March 2026", monthKey:"2026-03",
            status:"collecting",
            initiatedBy:CURRENT_USER.name, initiatedAt:"2026-03-24",
            deptComments:{ceo:""},
          };
          setReport(newReport);
          // Notify all dept heads
          REPORT_DEPTS.forEach(d=>{
            addNotification({id:uid(),type:"report",text:`${CURRENT_USER.name} has started the March 2026 Monthly Report — ${d.label} input required`,ts:"2026-03-24",read:false});
          });
          showToast("Report initiated","Department heads notified to submit comments");
        }}>
          <span>▶</span> Start March Report
        </button>
      </div>
    );
  }

  const submittedDepts=REPORT_DEPTS.filter(d=>report.deptComments[d.id]&&report.deptComments[d.id].trim());
  const allSubmitted=submittedDepts.length===REPORT_DEPTS.length;
  const progress=submittedDepts.length/REPORT_DEPTS.length;

  function submitDept(deptId, text){
    const updated={...report,deptComments:{...report.deptComments,[deptId]:text}};
    if(Object.values(updated.deptComments).filter((v,k)=>k!=="ceo"&&v&&v.trim()).length===REPORT_DEPTS.length){
      updated.status="ready";
    }
    setReport(updated);
    showToast("Comment saved","Your department input has been recorded");
  }

  function saveCeo(){
    setReport({...report,deptComments:{...report.deptComments,ceo:ceoText}});
    showToast("CEO summary saved","Executive summary updated");
  }

  function generateReport(){
    setReport({...report,status:"generated",generatedAt:"2026-03-24"});
    setShowPreview(true);
    showToast("Report generated","Monthly PDF report is ready to download");
  }

  return (
    <>
      <div className="bg-[rgba(64,117,193,0.05)] border border-[rgba(64,117,193,0.15)] rounded-xl px-5 py-[18px] mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3.5">
          <div>
            <div className="text-[15px] font-extrabold text-rc-text-primary">📋 {report.monthLabel} — Monthly Report</div>
            <div className="text-xs text-rc-text-secondary mt-0.5">
              {report.status==="generated"?"Report generated ✓":allSubmitted?"All departments submitted — ready to generate":
              `${submittedDepts.length} of ${REPORT_DEPTS.length} departments submitted`}
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {report.status==="generated"&&(
              <button className="wr-gen-btn" style={{color:C.blue}} onClick={()=>setShowPreview(true)}>👁 View Report</button>
            )}
            {allSubmitted&&report.status!=="generated"&&(
              <button className="inline-flex items-center gap-2 bg-gradient-to-br from-[rgba(16,201,129,0.2)] to-[rgba(59,127,250,0.2)] border border-[rgba(16,201,129,0.4)] text-[#6ee7b7] rounded-[9px] px-5 py-2.5 text-sm font-bold cursor-pointer font-sans transition-colors hover:bg-gradient-to-br hover:from-[rgba(16,201,129,0.3)] hover:to-[rgba(59,127,250,0.3)] disabled:opacity-40 disabled:cursor-not-allowed" onClick={generateReport}>⬇ Generate PDF Report</button>
            )}
            <button className="wr-gen-btn" onClick={()=>setReport(null)}>✕ Cancel</button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full h-1.5 bg-rc-border rounded overflow-hidden mb-1.5">
          <div className="h-full rounded bg-gradient-to-r from-rc-blue-500 to-rc-purple transition-[width] duration-[400ms]" style={{width:`${progress*100}%`}}/>
        </div>
        <div style={{fontSize:11,color:C.dim,marginBottom:14}}>{submittedDepts.length}/{REPORT_DEPTS.length} sections complete</div>

        {/* CEO Executive Summary */}
        <div style={{background:"rgba(59,127,250,.06)",border:"1px solid rgba(59,127,250,.2)",borderRadius:8,padding:"13px 15px",marginBottom:14}}>
          <div style={{fontSize:10.5,fontWeight:700,color:C.blue,textTransform:"uppercase",letterSpacing:".1em",marginBottom:8}}>◈ CEO Executive Summary</div>
          <textarea className="w-full bg-rc-surface border border-[#1c2e52] rounded-[7px] px-3 py-2.5 text-[12px] text-rc-text-primary font-sans resize-y outline-none min-h-[90px] leading-[1.6] transition-colors focus:border-rc-accent placeholder:text-[#253550]" rows={3} placeholder="Write the executive summary for this month…" value={ceoText} onChange={e=>setCeoText(e.target.value)}/>
          <button className="w-full mt-2 bg-[rgba(59,127,250,0.14)] border border-[rgba(59,127,250,0.28)] text-rc-accent-strong rounded-[7px] px-3.5 py-1.5 text-[12px] font-semibold cursor-pointer font-sans transition-colors hover:bg-[rgba(59,127,250,0.24)] disabled:opacity-40 disabled:cursor-not-allowed" style={{marginTop:7}} onClick={saveCeo} disabled={!ceoText.trim()}>Save Summary</button>
        </div>

        {/* Department cards */}
        <div className="grid grid-cols-3 gap-2.5">
          {REPORT_DEPTS.map(dept=>(
            <DeptCard key={dept.id} dept={dept} report={report} submitDept={submitDept}/>
          ))}
        </div>
      </div>
      {showPreview&&<PDFPreviewModal report={report} projects={projects} onClose={()=>setShowPreview(false)} showToast={showToast}/>}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RESOURCES VIEW — Consultant availability, assignments, and demand alignment
// ─────────────────────────────────────────────────────────────────────────────

